import { Language } from './types'

export type LanguageCode = 'en' | 'es' | 'pt'

export const translations = {
  en: {
    accounts: {
      accountUnavailable: 'Account unavailable',
      address: 'Address',
      arknote: 'Arknote',
      assetAmount: 'Asset amount',
      assetAmountTicker: 'Asset amount ({ticker})',
      assetAmountTickerUnverified: 'Asset amount ({ticker}, unverified)',
      assetId: 'Asset ID',
      completed: 'Completed',
      corridor: 'Corridor',
      date: 'Date',
      destination: 'Destination',
      direction: 'Direction',
      expiry: 'Expiry',
      funded: 'Funded',
      invoice: 'Invoice',
      l1HtlcAddress: 'L1 HTLC address',
      loadingPriceHistory: 'Loading price history…',
      networkFees: 'Network fees',
      paidOnchain: 'Paid on-chain',
      priceChartRange: 'Price chart range',
      priceHistoryUnavailable: 'Price history unavailable',
      priceRate: 'Price rate',
      priceUnavailable: 'Price unavailable',
      rail: 'Rail',
      receivedAt: 'Received at',
      recipientGets: 'Recipient gets',
      refundDeadline: 'Refund deadline',
      solver: 'Solver',
      status: 'Status',
      swapFees: 'Swap fees',
      swapFrom: 'Swap from',
      swapTo: 'Swap to',
      total: 'Total',
      transactionId: 'Transaction ID',
      type: 'Type',
      unverifiedAssetAmount: 'Unverified asset amount',
      valueUnavailable: 'Unavailable',
      when: 'When',
    },
    appIntent: {
      address: 'Address',
      addressFailed: 'Could not read your address.',
      badCallback: 'The return link must be an https address.',
      badRequest: 'This payment link is not a valid Bitcoin request.',
      deny: "Don't share",
      errorTitle: 'App request',
      goHome: 'Go to wallet',
      missingCallback: 'This link has nowhere to return to.',
      missingRequest: 'This payment link is missing a payment request.',
      open: 'Open',
      openInApp: 'Open in the app',
      openInAppHint: 'Finish this request in the installed Arkade app.',
      openInAppSafari:
        "Safari can't open the installed app from this page. Open Arkade from your Home Screen, or finish the request here.",
      pubkey: 'Public key',
      returnTo: 'Return to {host}',
      sentReturn: '{amount} sent. Tap to return to {host}',
      share: 'Share address',
      shareExplainer: 'This shares your receive address and public key. It cannot spend your funds.',
      title: 'Connect',
      tooLong: 'This link is too long.',
      unknownAction: 'This link is not a known app request.',
      wantsAddress: '{host} is asking for your Arkade address.',
    },
    apps: {
      dfx: 'DFX',
      dfxConnectionFailed: 'Failed to connect to DFX. Please go back and try again.',
    },
    backup: {
      backupFailed: 'Backup to Nostr failed',
      cantRecover: "If you lose it you can't recover it",
      confirm: 'Confirm',
      copyToClipboard: 'Copy to clipboard',
      doNotShare: "Don't share it with anyone",
      enableNostrBackups: 'Enable Nostr backups',
      enoughToRestore: 'This is enough to restore your wallet.',
      forYourEyesOnly: 'For your eyes only, do not share.',
      keepSafe: 'Keep your {secret} safe',
      keyBacksUpWallet: 'Your private key is used to back up your wallet. Keep it secret and secure at all times.',
      nostrBackupUpdated: 'Nostr backup updated',
      nostrBackupsSubtext: 'Turn Nostr backups on or off',
      phraseBacksUpWallet:
        'Your recovery phrase is used to back up your wallet. Keep it secret and secure at all times.',
      phraseWarning:
        "Your recovery phrase can be used to access everything in your wallet. Don't share it with anyone.",
      privateKey: 'Private key',
      privateKeyWarning:
        "Your private key can be used to access everything in your wallet. Don't share it with anyone.",
      recoveryPhrase: 'Recovery phrase',
      viewPrivateKey: 'View private key',
      viewRecoveryPhrase: 'View recovery phrase',
    },
    browser: {
      copied: 'Copied',
      copyLink: 'Copy link',
      notSupported: "Won't work in this browser.",
      stepBrowser: 'Open Safari, Chrome, or your browser',
      stepCopy: 'Copy the link below',
      stepPaste: 'Paste the link and go',
      welcome: 'Welcome to Arkade 👾',
    },
    common: {
      address: 'Address',
      all: 'All',
      amount: 'Amount',
      back: 'Go back',
      balance: 'Balance',
      cancel: 'Cancel',
      close: 'Close',
      continue: 'Continue',
      copied: 'Copied!',
      copiedToClipboard: 'Copied to clipboard',
      dismiss: 'Dismiss',
      failedToCopy: 'Failed to copy',
      generateOrImportCoins: 'Generate or import coins to continue',
      gotIt: 'Got it',
      home: 'Home',
      importOrMintToStart: 'Import or mint one to get started',
      learnMore: 'Learn more',
      loading: 'Loading...',
      makeTransactionToStart: 'Make a transaction to get started.',
      network: 'Network',
      noAssetsYet: 'No assets yet',
      noLogsAvailable: 'No logs available',
      noTransactionsYet: 'No transactions yet',
      noVirtualCoins: 'No virtual coins available',
      off: 'Off',
      on: 'On',
      paste: 'Paste',
      sats: 'sats',
      save: 'Save',
      shortly: 'shortly',
      startUsingToGenerateLogs: 'Start using the app to generate logs.',
      success: 'Success',
      total: 'Total',
      tryAgain: 'Try again',
      unknown: 'Unknown',
      value: 'Value',
    },
    components: {
      arkadeAddress: 'Arkade address',
      btcAddress: 'BTC address',
      cameraBlocked: 'Camera access is blocked. Allow it for this site in your browser settings, then try again.',
      cameraNotAvailable: 'Camera not available',
      clear: 'Clear',
      clearAmount: 'Clear amount',
      confirmPassword: 'Confirm password',
      copyAddress: 'Copy address',
      filterActivity: 'Filter activity',
      focusableElement: 'Focusable element',
      hideBalances: 'Hide balances',
      lightningInvoice: 'Lightning invoice',
      lnurlAddress: 'LNURL',
      noPasswordYolo: 'No password, YOLO',
      password: 'Password',
      passwordMinChars: '8 characters minimum',
      passwordOneNumber: 'contain at least 1 number',
      passwordSpecialChar: 'contain at least 1 special character',
      passwordsMustMatch: 'Passwords must match',
      protectWalletWithPassword: 'Protect your wallet with a password',
      recoveryPhraseOrKey: 'Recovery phrase or private key',
      reload: 'Reload',
      reloadToContinue: 'Please reload to continue',
      save: 'Save',
      savePassword: 'Save password',
      scanQr: 'Scan QR',
      setMaximumAmount: 'Set maximum amount',
      showBalances: 'Show balances',
      somethingWentWrong: 'Something went wrong',
      strongPasswordWith: 'Set a strong password with:',
      toggleCurrency: 'Toggle currency',
      toggleVisibility: 'Toggle visibility',
      unexpectedError: 'The app ran into an unexpected error',
    },
    contracts: {
      active: 'Active',
      deprecatedSigner: 'deprecated signer',
      deprecatedSignerCutoff: 'deprecated signer · past cutoff',
      inactive: 'Inactive',
      noContracts: 'No contracts found.',
      parameters: 'Parameters',
      pastRefundLocktime: 'Past refund locktime',
      refundLocktime: 'Refund locktime',
      script: 'Script',
      searchPlaceholder: 'Search type, address, script…',
    },
    delegate: {
      active: 'Active',
      addressLabel: 'address: {value}',
      delegateDescription: 'A delegate is a trusted third party you appoint to help keep your VTXOs safe and secure.',
      delegatesRenewOnly: 'Delegates can only renew your VTXOs, they cannot spend your funds or control your wallet',
      feeLabel: 'fee: {value}',
      inactive: 'Inactive',
      nextRenewal: 'next renewal {time}',
      noDelegateFound: 'No delegate found for this network.',
      noUpcomingRenewal: 'No upcoming renewal',
      pubkeyLabel: 'pubkey: {value}',
      useDefaultArkadeDelegate: 'Use default Arkade delegate',
      useDefaultSubtext: "Use Arkade's default delegate to manage renewals",
      walletReloadToApply: 'The wallet will reload to apply the change.',
      whatIsADelegate: 'What is a Delegate?',
    },
    errors: {
      amountBetween: 'Amount must be between {min} and {max} sats',
      outdatedWallet:
        'Your wallet is outdated and needs to be updated to be compatible with the latest Arkade version.',
      recentlySettledHours: 'Your funds were recently settled onchain — please try again in a few hours',
      recentlySettledRetry: 'Your funds were recently settled onchain — please try again {when}',
    },
    formatting: {
      ago: '{value} ago',
      ahead: 'in {value}',
      day: 'day',
      days: 'days',
      hour: 'hour',
      hours: 'hours',
      justNow: 'just now',
      minute: 'minute',
      minutes: 'minutes',
      second: 'second',
      seconds: 'seconds',
      unit: '{count} {unit}',
    },
    init: {
      addressRotation: 'Address rotation',
      arkadeServerUnreachable: 'Arkade server unreachable',
      biometricsDescription:
        'This will allow you to log in easily through biometrics without a need to remember the password.',
      connectingToServer: 'Connecting to server',
      connectingToServerStatus: 'Connecting to server',
      connectionFailed: 'Connection failed',
      createNewWallet: 'Create new wallet',
      createPasskey: 'Create passkey',
      createReadyText: 'Your wallet has been successfully created and is now ready to use.',
      createWallet: 'Create wallet',
      goToWallet: 'Go to wallet',
      newWalletLive: 'Your new wallet is live!',
      otherLoginOptions: 'Other login options',
      recoveryChecksumError: 'This recovery phrase doesn’t validate. Check the words and their order.',
      recoveryInputHint: 'Type or paste your recovery phrase or private key.',
      recoveryInstructions:
        "Enter your 12-word recovery phrase, or a private key starting with 'nsec' or a raw hex key. Do not share it with anyone.",
      recoveryKeyError: 'Enter a valid nsec private key or a 64-character hexadecimal private key.',
      recoveryOneWord: '1 word entered',
      recoveryWordCount: '{count} words entered',
      recoveryWordCountError: 'Enter the complete recovery phrase: 12, 15, 18, 21 or 24 words.',
      recoveryWordError: 'Word {number} isn’t recognized. Check its spelling.',
      restoreReadyText: 'Your wallet has been successfully restored and is now ready to use.',
      restoreSuccessText: 'Your wallet, settings and swaps have been successfully restored.',
      restoreWallet: 'Restore wallet',
      restoringWallet: 'Restoring wallet...',
      rotateReceiveAddresses: 'Rotate receive addresses',
      rotateReceiveSubtext:
        'Derive a fresh address for every incoming payment (HD wallet). Improves on-chain privacy. Best used with Nostr backup enabled so rotated addresses are recoverable on restore. For advanced users.',
      rotationSubtext:
        'Inherit uses your saved wallet setting (typically restored from backup). If backup restore is unavailable, it falls back to your local/default setting. Pick HD if this wallet rotated receive addresses and you need to force HD recovery.',
      tagline1: 'Fast payments, swaps, and more',
      tagline2: 'Access Lightning, mint assets, and more. All secured by Bitcoin',
      tagline3: 'Stay in control. Settle and withdraw on your terms',
      useBiometrics: 'Use biometrics',
      usePassword: 'Use password',
      walletRestored: 'Wallet restored successfully!',
      wordSuggestions: 'Word suggestions',
    },
    loading: {
      burning: 'Burning...',
      connectingToDfx: 'Connecting to DFX...',
      connectingToServiceWorker: 'Connecting to service worker...',
      fetchAssetDetails: 'Fetching asset details...',
      fetchingCoins: 'Fetching coins...',
      fetchingTransactions: 'Fetching transactions...',
      loadingAsset: 'Loading asset...',
      loadingAssetMetadata: 'Loading asset metadata...',
      loadingAssets: 'Loading assets...',
      loadingWalletData: 'Loading wallet data...',
      mintingAsset: 'Minting asset...',
      mintingControlAsset: 'Minting control asset...',
      payingToMainnet: 'Paying to mainnet. This may take a few moments.',
      recoveringAddresses: 'Recovering addresses...',
      reissuing: 'Reissuing...',
      renewing: 'Renewing your virtual coins. This may take a few moments.',
      retryingConnection: 'Retrying connection...',
      settlingTransactions: 'Settling transactions. This may take a few moments.',
      startingWallet: 'Starting wallet...',
      updatingBalance: 'Updating balance...',
    },
    logs: {
      logAt: 'Log at {time} with message {msg}. Press Escape to exit keyboard navigation.',
      pressEnter: 'Pressing Enter enables keyboard navigation of the logs',
    },
    mint: {
      additionalAmount: 'Additional Amount',
      amount: 'Amount *',
      amountMustBePositive: 'Amount must be a positive number',
      amountToBurn: 'Amount to Burn',
      arkadeMint: 'Arkade Mint',
      arkadeMintDisabled: 'Arkade Mint is disabled.',
      assetCreated: 'Asset Created',
      assetId: 'Asset ID',
      assetIdMustBeHex: 'Asset ID must be a 68-character hex string',
      assetIdRequired: 'Asset ID is required',
      assetIdTapToCopy: 'Asset ID (tap to copy)',
      assetMinted: 'Asset minted!',
      assetNotFound: 'Asset not found',
      backToArkadeMint: 'Back to Arkade Mint',
      burn: 'Burn',
      burnTitle: 'Burn {ticker}',
      burnWarning: 'You are about to burn {amount} {ticker}. This action is irreversible.',
      confirmBurn: 'Confirm Burn',
      confirmReissue: 'Confirm Reissue',
      controlAmount: 'Control Amount',
      controlAmountPositive: 'Control amount must be positive',
      controlAsset: 'Control Asset',
      decimals: 'Decimals',
      decimalsRange: 'Decimals must be 0-{max}',
      enableArkadeMint: 'Enable Arkade Mint',
      enableArkadeMintSubtext: 'Turn Arkade Mint on or off',
      enableIt: 'Enable it',
      enterAmount: 'Enter an amount',
      enterControlAmount: 'Enter control asset amount',
      enterName: 'Enter a name',
      enterTicker: 'Enter a ticker',
      exceedsBalance: 'Cannot burn more than your balance ({balance} {ticker})',
      existing: 'Existing',
      hideIcon: 'Hide icon',
      iconUrl: 'Icon URL',
      import: 'Import',
      importAsset: 'Import Asset',
      invalidDecimals: 'Decimals must be an integer between 0 and {max}',
      invalidIconUrl: 'Invalid icon URL',
      max: 'Max',
      mint: 'Mint',
      myToken: 'My Token',
      name: 'Name *',
      nameLabel: 'Name',
      nameTooLong: 'Name must be 40 characters or less',
      new: 'New',
      none: 'None',
      pasteAssetId: 'Paste asset ID...',
      receive: 'Receive',
      reissue: 'Reissue',
      reissueTitle: 'Reissue {ticker}',
      reissueWarning: 'You are about to mint {amount} additional {ticker}.',
      remove: 'Remove',
      selectFromWallet: 'Select from wallet...',
      send: 'Send',
      settingsTitle: 'Arkade Mint settings',
      showIcon: 'Show icon',
      supply: 'Supply',
      ticker: 'Ticker *',
      tickerLabel: 'Ticker',
      tickerTooLong: 'Ticker must be 8 characters or less',
      title: 'Mint Asset',
      toViewYourAssets: 'to view your assets.',
      unknown: 'Unknown',
      unknownAsset: 'Unknown Asset',
      viewAsset: 'View Asset',
    },
    notes: {
      arkNote: 'Ark note',
      noteRedeemed: 'Note redeemed!',
      processing: 'Processing. This may take a few moments.',
      redeemNote: 'Redeem Note',
      redeemedSuccessfully: 'redeemed successfully',
      redeeming: 'Redeeming...',
      soundsGood: 'Sounds good',
      title: 'Note',
    },
    notifications: {
      apiNotSupported: 'Notifications API not supported',
      permissionDenied: 'Notifications permission denied',
    },
    receive: {
      addAmount: 'Add amount',
      addName: 'Add a name',
      amount: 'Amount',
      arkadeAddress: 'Arkade address',
      backToMethod: 'Back to {method}',
      bitcoinAddress: 'Bitcoin address',
      chooseName: 'Choose a name',
      chooseNameOptional: 'Choose a name (optional)',
      claimCode: 'Claim code',
      claimCodeInvalid: 'That claim code is not valid.',
      claimCodeNeedsName: 'This code is for a name you choose. Add one and claim again.',
      claimCodeOptional: 'Claim code (optional)',
      claimFailed: 'Claiming the Lightning payment failed: {error}',
      claimModeUnavailable: 'This server does not offer that option.',
      claimName: 'Claim name',
      claimNotAllowed: 'That name is not allowed here.',
      claimRateLimited: 'Too many attempts — try again shortly.',
      claimTaken: 'That name is already taken.',
      clearAmount: 'Clear amount',
      copy: 'Copy',
      copyAddress: 'Copy address',
      copyAria: 'Copy {title}',
      copyQrCode: 'Copy QR code',
      editAmount: 'Edit amount',
      failedToGetAddress: 'Failed to get address: {error}',
      generatingInvoice: 'Generating invoice…',
      getNewQuote: 'Get a new quote',
      gettingQuote: 'Getting quote…',
      haveClaimCode: 'I have a claim code',
      invalidNote: 'Invalid note',
      lightningInvoice: 'Lightning invoice',
      lightningPaymentLost: 'Lightning payment lost: the solver reclaimed it before it could be claimed',
      lightningUnavailable: 'Lightning unavailable: {error}',
      lnaddressClaimFailed: 'Could not get a lightning address.',
      lnaddressUnavailable: 'Lightning address unavailable: {error}',
      methodArkade: 'Arkade',
      methodBitcoin: 'Bitcoin',
      methodLightning: 'Lightning',
      noInvoiceForAmount: 'No invoice for this amount: {error}',
      noLightningSolver: 'No Lightning solver available',
      noPaymentMethods: 'No valid payment methods available for this amount',
      noTokenQuote: 'No quote for this token: {error}',
      openInWallet: 'Open in wallet',
      payWithStablecoins: 'Pay with stablecoins',
      pickForMe: 'Pick one for me',
      quoteExpired: 'Quote expired',
      quoteExpiredSub: 'That deposit address is no longer valid. Do not send to it.',
      requestingAmount: 'Requesting {amount} {unit}',
      setAmount: 'Set amount',
      share: 'Share',
      tokenAmount: 'Exact amount ({unit})',
      tokenDepositAddress: '{token} deposit address',
      tokenQuoteValid: 'Quote by {provider}, valid for {time}',
      tokenSendExactly: 'Send exactly {amount} {unit} on {chain}',
      unified: 'Unified',
    },
    scanner: {
      cameraError: 'Camera not available',
      tryAgain: 'Try again',
    },
    send: {
      amountAboveLnurlMax: 'Amount above LNURL max limit',
      amountAboveMax: 'Amount above max limit',
      amountBelowLnurlMin: 'Amount below LNURL min limit',
      amountBelowMin: 'Amount below min limit',
      amountBelowOneSat: 'Amount below 1 satoshi',
      amountTooLow: 'Amount too low',
      amountToSend: 'Amount to send',
      arkadeServerKeyMismatch: 'Arkade server key mismatch',
      asset: 'Asset',
      assetsOnlyToArkade: 'Assets can only be sent to Arkade addresses',
      available: '{amount} available',
      balanceReserve: 'Balance reserve',
      balanceReserveText:
        '{dust} sats are kept in reserve to protect your assets. Your max sendable amount is {max} sats.',
      bitcoin: 'Bitcoin',
      continue: 'Continue',
      directionSendingAssets: 'Sending assets',
      errorSendingTransaction: 'Error sending transaction',
      feeChanged: 'The network fee changed. Review the new total and tap to sign again.',
      feesDeductedFromAmount: 'Fees will be deducted from the amount sent',
      gettingQuote: 'Getting quote',
      insufficientAssetBalance: 'Insufficient asset balance',
      insufficientFunds: 'Insufficient funds',
      insufficientFundsDetail: 'Insufficient funds, you just have {balance} sats',
      insufficientFundsForLnurl: 'Insufficient funds for LNURL',
      insufficientFundsMax: 'Insufficient funds. After network fees you can send up to {max} sats',
      invalidAmount: 'Invalid amount',
      invalidArkadeAddressFromLnurl: 'Invalid Arkade address received from LNURL',
      invalidRecipient: 'Invalid recipient address',
      invoiceMustHaveAmount: 'Invoice must have amount defined',
      lnurlNotFound: 'LNURL not found',
      missingAddress: 'Missing address',
      missingAmount: 'Missing amount',
      missingAssetAmount: 'Missing asset amount',
      missingSatoshisAmount: 'Missing satoshis amount',
      missingTotalAmount: 'Missing total amount',
      onTheWay: '{amount} on the way',
      onTheWayKeepOpen: '{amount} on the way — keep the wallet open until it completes',
      payingInsideArkade: 'Paying inside Arkade',
      payingToLightning: 'Paying to Lightning',
      payingToMainnet: 'Paying to mainnet',
      paymentOnTheWay: 'Payment is on the way',
      paymentSent: 'Payment sent',
      pendingOnchain: 'An onchain payment is still on the way — keep the wallet open until it completes',
      quoteForDifferentInvoice: 'Quote is for a different invoice',
      quoteForDifferentPayment: 'Quote is for a different payment',
      recipientAddress: 'Recipient address',
      sendMax: 'Send max',
      sendingOffchainNotAllowed: 'Sending offchain not allowed',
      sendingOnchainNotAllowed: 'Sending onchain not allowed',
      sentSuccessfully: '{amount} sent successfully',
      serverUnreachable: 'Server unreachable',
      signTransaction: 'Sign transaction',
      tapToSign: 'Tap to Sign',
      title: 'Send',
      unableToDecodeInvoice: 'Unable to decode invoice',
      unableToGetReceivingAddresses: 'Unable to get receiving addresses',
      unableToParseBip21: 'Unable to parse bip21',
      unknownAsset: 'Unknown asset',
      verifiedByBranta: 'Verified by Branta',
      verifyingAddress: 'Verifying address...',
    },
    settings: {
      about: 'About',
      advanced: 'Advanced',
      allowNotifications: 'Allow notifications',
      appearance: 'Appearance',
      arkadeMint: 'Arkade Mint',
      auto: 'Auto',
      backup: 'Backup',
      bitcoinUnit: 'Bitcoin unit',
      boardingExitDelay: 'Boarding exit delay',
      bugReports: 'Bug Reports',
      bugReportsText:
        'Report any issues or bugs you encounter. Include steps to reproduce the problem for faster resolution.',
      cannotUndo: 'This operation cannot be undone.',
      changePassword: 'Change password',
      clear: 'Clear',
      connectToServer: 'Connect to server',
      contracts: 'Contracts',
      currency: 'Currency',
      customerSupport: 'Customer support',
      customerSupportText:
        'Get help with your wallet, report bugs, or ask questions. Our support team is here to assist you.',
      dark: 'Dark',
      delegates: 'Delegates',
      didYouBackup: 'Did you backup your wallet?',
      display: 'Display',
      dust: 'Dust',
      english: 'English',
      exportToCsv: 'Export to CSV file',
      failedToLoadSupportChat: 'Failed to load support chat',
      failedToUpdatePassword: 'Failed to update password',
      feedback: 'Feedback',
      forfeitAddress: 'Forfeit address',
      general: 'General',
      gitCommitHash: 'Git commit hash',
      hapticFeedback: 'Haptic feedback',
      hapticSubtext: 'Vibration on button taps and interactions',
      iHaveBackedUp: 'I have backed up my wallet',
      language: 'Language',
      light: 'Light',
      lightningAddress: 'Lightning address',
      lnurlAddName: 'Add a name',
      lnurlLimits: 'What payers can send',
      lnurlNone: 'No lightning address yet.',
      lnurlPrimary: 'Primary address',
      lnurlServer: 'Server',
      lnurlYours: 'Your lightning addresses',
      lock: 'Lock',
      lockExplanation: "After locking you'll need to re-enter your password to unlock.",
      lockWallet: 'Lock Wallet',
      lockYourWallet: 'Lock your wallet',
      logs: 'Logs',
      noPasswordDefined: 'No password defined',
      notes: 'Notes',
      notifications: 'Notifications',
      notificationsIntro:
        "Get notified when an update is available or a payment is received. You'll need to grant permission if asked.",
      notificationsUnsupported:
        "Your browser does not support the Notifications API. If on iOS you'll need to 'Add to homescreen' and be running iOS 16.4 or higher.",
      openSupportChat: 'Open Support Chat',
      passwordChanged: 'Password changed',
      passwordChangedToBiometrics: 'Password changed to biometrics',
      passwordRemoved: 'Password removed',
      portuguese: 'Portuguese',
      preferences: 'Preferences',
      receive: 'Receive',
      receiveViaTokens: 'Accept tokens',
      receiveViaTokensSubtext:
        'With an amount set, payers can also send {units}, which a third party swaps to Arkade BTC.',
      resetFailed: 'Could not reset the wallet. Some data may have been cleared — close any other tabs and try again.',
      resetWallet: 'Reset wallet',
      sameServer: 'Same server',
      secureChat: 'Secure Chat',
      secureChatText: 'Your conversations are secure and private. Chat history is maintained across sessions.',
      security: 'Security',
      server: 'Server',
      serverFound: 'Server found',
      serverPubkey: 'Server pubkey',
      serverResetWarning: 'Your wallet will be reset. Make sure you backup your wallet first.',
      serverUnavailableOnMainnet: 'Server settings are unavailable on mainnet.',
      serverUrl: 'Server URL',
      sessionDuration: 'Session duration',
      setPassword: 'Set Password',
      setPasswordToLock: 'You need to set a password to lock.',
      solvers: 'Solvers',
      spanish: 'Spanish',
      success: 'Success',
      support: 'Support',
      supportChatNotConfigured: 'Support chat is not configured',
      theme: 'Theme',
      title: 'Settings',
      tokenExplainer:
        'Payers send tokens to {provider}, a third party that holds the deposit while it pays a Lightning invoice. An intent-solver swap then delivers that payment to you as Arkade BTC. A late, short or excess deposit is resolved with {provider}.',
      tokenExplainerConfirm: 'Turn on',
      tokenExplainerTitle: 'Before you accept tokens',
      trackProgress: 'Track Progress',
      trackProgressText:
        'All your support tickets and conversations are saved. You can view past conversations anytime.',
      unableToConnect: 'Unable to connect',
      unilateralExitDelay: 'Unilateral exit delay',
      useBiometrics: 'Use biometrics',
      vtxos: 'Virtual Coins',
      walletMode: 'Wallet mode',
    },
    solvers: {
      addNew: '+ Add new',
      buildShipsPlural: 'This build ships {count} solver cards; add your own to reach more solvers.',
      buildShipsSingular: 'This build ships {count} solver card; add your own to reach more solvers.',
      builtIn: 'Built-in',
      builtInPlusOwnPlural: '{builtIn} built-in solver cards, plus {own} of your own.',
      builtInPlusOwnSingular: '{builtIn} built-in solver card, plus {own} of your own.',
      cardInfo: 'Solver card info',
      confirmRemove: 'Confirm Remove',
      currentLabel: 'Current',
      edit: 'Edit',
      failedRemove: 'Failed to remove card: storage is full or unavailable.',
      failedSave: 'Failed to save card: storage is full or unavailable.',
      fieldBase: 'Base',
      fieldDiscoveryKey: 'Discovery key',
      fieldFee: 'Fee',
      fieldName: 'Name',
      fieldQuote: 'Quote',
      fieldVersion: 'Version',
      hideRaw: 'Hide raw JSON',
      invalidCard: 'invalid card: {message}',
      invalidJson: 'invalid JSON: {message}',
      marketsLabel: 'Markets',
      noStoredCards: 'You have no solver cards stored in your wallet.',
      originalLabel: 'Original',
      publishedMarkets: 'Markets published by the solver',
      remoteChecking: 'Checking for the current card…',
      remoteError: 'Could not load the current card.',
      remoteNoRelays: 'Local only — no Nostr relays in this card.',
      remoteNotFound: 'No current card found on its relays.',
      remove: 'Remove',
      removeWarning: 'Are you sure you want to remove the card "{label}"? This action cannot be undone.',
      storedPlural: 'You have {count} solver cards stored in your wallet.',
      storedSingular: 'You have {count} solver card stored in your wallet.',
      viewInfo: 'Info',
      viewRaw: 'View raw JSON',
    },
    swap: {
      amountTooSmall: 'Amount too small',
      asset: 'asset',
      assetAmount: 'asset amount',
      chooseAsset: 'Choose asset',
      chooseAssetToReceive: 'Choose asset to receive',
      chooseAssetToSwap: 'Choose asset to swap',
      comingSoon: 'Swaps are coming soon',
      comingSoonText: 'We are polishing the swap experience before turning it on here.',
      confirmSwap: 'Confirm swap',
      createdTapHome: 'Swap created. Tap to go home.',
      currencyAmount: '{currency} amount',
      deleteDigit: 'Delete digit',
      fees: 'Fees',
      insufficientBalance: 'Insufficient balance',
      keypadFor: 'Swap keypad for {amount}',
      max: 'Max',
      maximum: 'Maximum',
      minimum: 'Minimum',
      noAssetsMatch: 'No assets match this search',
      pairUnavailable: 'Swap unavailable for this pair',
      partialSwapNeedsBtc:
        "You don't have enough bitcoin to do a partial swap. Please swap all or acquire some bitcoin.",
      pending: 'Pending',
      pickAssetForSide: 'Pick the asset for this side of the swap.',
      quoteUnavailable: 'Quote unavailable',
      receive: 'Receive',
      reviewSwap: 'Review swap',
      reviewSwapDescription: 'Check the route and estimated totals.',
      routeConnector: 'to',
      searchAssets: 'Search assets',
      selectAssetToTradeFrom: 'Select the asset you want to trade from.',
      showFirst: 'Show {mode} first',
      swapCreated: 'Swap created',
      swapRoute: 'Swap route',
      swapsUnavailable: 'Swaps are unavailable',
      swapsUnavailableText: 'Add another supported asset to swap between balances.',
      switchSwapDirection: 'Switch swap direction',
      title: 'Swap',
      useMaximum: 'Use maximum {balance}',
      waitingForFill: 'Waiting for fill',
    },
    table: {
      keyboardNavIntro: 'Pressing Enter enables keyboard navigation of the table',
      keyboardNavTitle: 'Title {title} with status {value}. Press Escape to exit keyboard navigation.',
    },
    transaction: {
      addReminder: 'Add reminder',
      amountBurned: 'Amount burned',
      amountExited: 'Amount exited',
      amountIssued: 'Amount issued',
      amountReceived: 'Amount received',
      amountReissued: 'Amount reissued',
      amountSent: 'Amount sent',
      assetId: 'Asset ID',
      assetIdTicker: 'Asset ID ({ticker})',
      assetIdTickerUnverified: 'Asset ID ({ticker}, unverified)',
      assetIdUnverified: 'Asset ID (unverified)',
      boardingExpired: 'Boarding transaction expired.',
      burn: 'Burn',
      cancelSwap: 'Cancel swap',
      cancelSwapBody: 'If the swap is still pending, this will return its locked funds to your wallet.',
      cancelSwapTitle: 'Cancel swap?',
      cancelled: 'Cancelled',
      cancelling: 'Cancelling…',
      completeBoarding: 'Complete boarding',
      completed: 'Completed',
      exited: 'Exited',
      expired: 'Expired',
      failed: 'Failed',
      fromAssetId: 'From asset ID',
      fromAssetIdUnverified: 'From asset ID (unverified)',
      issuance: 'Issuance',
      keepSwap: 'Keep swap',
      keyboardNavAria: 'Transaction {type} of amount {amount}. Press Escape to exit keyboard navigation.',
      keyboardNavHint: 'Pressing Enter enables keyboard navigation of the transaction list',
      lightningReceive: 'Lightning receive',
      lightningReceiveFailed: 'Lightning receive failed',
      lightningReceiveLost: 'Lightning receive lost',
      lightningReceivePending: 'Lightning receive pending',
      lightningReceiveRefunded: 'Lightning receive refunded',
      lightningSend: 'Lightning send',
      lightningSendFailed: 'Lightning send failed',
      lightningSendLost: 'Lightning send lost',
      lightningSendPending: 'Lightning send pending',
      lightningSendRefunded: 'Lightning send refunded',
      lnurlReceivedAt: 'Received at {address}',
      lnurlReceivedVia: 'Received via {rail}',
      lnurlSentTo: 'Sent to {address}',
      lnurlSentVia: 'Sent via {rail}',
      paymentReceived: 'Payment received',
      paymentReceivedAria: 'Payment received successfully. Tap to go home.',
      pending: 'Pending',
      pendingBoarding: 'Pending boarding',
      pendingBoardingText: 'Onboard transaction confirmed on-chain.',
      preconfirmed: 'Preconfirmed',
      received: 'Received',
      receivedSuccessfully: 'received successfully',
      receiverConfirmed: 'Receiver confirmed',
      receiverConfirmedText: "The receiver's server confirmed this payment settled.",
      receiverNotConfirmed: 'Not confirmed',
      receiverNotConfirmedText: 'The receiver has not confirmed this payment settled.',
      recoverInProgress: 'Recovery is still running. Check back in a few minutes.',
      recoverNotIncluded: 'The recovery round did not include this deposit yet. Try again in a few minutes.',
      recoverNothing: 'There is nothing left to recover for this swap.',
      recoverSwap: 'Recover funds',
      recoverSwapBody:
        'This settles the expired deposit back into your wallet in the next batch round, together with any other expired coins. It can take a few minutes.',
      recoverSwapTitle: 'Recover swap funds?',
      recoverUnsupported: 'This wallet cannot run a recovery round.',
      recoverable: 'Recoverable',
      recoverableText:
        'The deposit expired before the swap was filled. Your funds are safe: recover them to spend them again.',
      recovering: 'Recovering…',
      refunded: 'Refunded',
      renew: 'Renew',
      retryCancel: 'Retry cancel',
      retryRecover: 'Retry recovery',
      sent: 'Sent',
      settleTransaction: 'Settle transaction',
      settled: 'Settled',
      settledSuccessfully: 'Transaction settled successfully',
      settling: 'Settling...',
      soundsGood: 'Sounds good',
      success: 'Success',
      swap: 'Swap',
      swapCancelled: 'Swap cancelled',
      swapFailed: 'Swap failed',
      swapPending: 'Swap pending',
      swapRecoverable: 'Swap recoverable',
      toAssetId: 'To asset ID',
      toAssetIdUnverified: 'To asset ID (unverified)',
      totalReceived: 'Total received',
      totalTicker: 'Total ({ticker})',
      unconfirmed: 'Unconfirmed',
      unconfirmedText: 'Onchain transaction unconfirmed. Please wait for confirmation.',
    },
    unlock: {
      connectionFailed: 'Connection failed. Please try again.',
      enterPassword: 'Enter your password',
      insertPassword: 'Insert password',
      invalidPassword: 'Invalid password',
      passwordPlaceholder: 'password',
      title: 'Unlock',
      unlockWallet: 'Unlock wallet',
      unlockWithPasskey: 'Unlock with your passkey',
    },
    upsells: {
      buySellDescription: 'Use a bank transfer in CHF or EUR via SEPA.',
      buySellTitle: 'Buy or sell bitcoin',
      title: 'Do more with your money',
    },
    vtxos: {
      automaticRenewal: 'Automatic renewal occurs for virtual coins expiring within {time}.',
      belowDustLimit: 'Below dust limit',
      boardingUtxos: 'Your boarding utxos with amount and expiration',
      coins: 'Coins',
      coinsRenewed: 'Coins renewed successfully',
      coinsWithAmount: 'Your virtual coins with amount and expiration',
      completeBoarding: 'Complete boarding',
      completeBoardingAndRenew: 'Complete boarding & renew',
      date: 'Date',
      expiringSoon: 'expiring soon',
      failedToFetchCoins: 'Failed to fetch coins',
      firstExpiration: 'First virtual coin expiration: {time}.',
      nextMarketHour: 'Next market hour: {date} ({ago}) for {duration}.',
      nextRenewal: 'Next Renewal',
      renewVirtualCoins: 'Renew Virtual Coins',
      renewing: 'Renewing',
      settled: 'settled',
      settlementFees: 'Settlement during market hours offers lower fees.',
      subdust: 'subdust',
      swept: 'recoverable',
      title: 'Virtual Coins',
      unconfirmed: 'unconfirmed',
      virtualCoinRenewal: 'Virtual Coin Renewal',
    },
    wallet: {
      accounts: 'Accounts',
      activity: 'Activity',
      addArkadeToHome: 'Add Arkade to your home screen',
      install: 'Install',
      installAndroidDescription: "Tap 'Install' to add Arkade to your home screen.",
      installIosDescription: "Tap the share icon in Safari's toolbar, then 'Add to Home Screen'.",
      openSettings: 'Open settings',
      receive: 'Receive',
      recentActivity: 'Recent activity',
      scan: 'Scan',
      send: 'Send',
      swap: 'Swap',
      syncing: 'Syncing...',
      tapToGoHome: 'Tap to go home.',
      transaction: 'Transaction',
      viewAll: 'View all',
      viewAllActivity: 'View all activity',
      viewRecentActivity: 'View recent activity',
      walletActions: 'Wallet actions',
    },
  },
  es: {
    accounts: {
      accountUnavailable: 'Cuenta no disponible',
      address: 'Dirección',
      arknote: 'Arknote',
      assetAmount: 'Monto del activo',
      assetAmountTicker: 'Monto del activo ({ticker})',
      assetAmountTickerUnverified: 'Monto del activo ({ticker}, sin verificar)',
      assetId: 'ID del activo',
      completed: 'Completado',
      corridor: 'Corredor',
      date: 'Fecha',
      destination: 'Destino',
      direction: 'Dirección',
      expiry: 'Vencimiento',
      funded: 'Financiado',
      invoice: 'Factura',
      l1HtlcAddress: 'Dirección L1 HTLC',
      loadingPriceHistory: 'Cargando historial de precios…',
      networkFees: 'Comisiones de red',
      paidOnchain: 'Pagado on-chain',
      priceChartRange: 'Rango del gráfico de precios',
      priceHistoryUnavailable: 'Historial de precios no disponible',
      priceRate: 'Tasa de precio',
      priceUnavailable: 'Precio no disponible',
      rail: 'Vía',
      receivedAt: 'Recibido en',
      recipientGets: 'El destinatario recibe',
      refundDeadline: 'Fecha límite de reembolso',
      solver: 'Solver',
      status: 'Estado',
      swapFees: 'Comisiones de swap',
      swapFrom: 'Intercambiar desde',
      swapTo: 'Intercambiar hacia',
      total: 'Total',
      transactionId: 'ID de transacción',
      type: 'Tipo',
      unverifiedAssetAmount: 'Monto de activo sin verificar',
      valueUnavailable: 'No disponible',
      when: 'Cuándo',
    },
    appIntent: {
      address: 'Dirección',
      addressFailed: 'No se pudo leer tu dirección.',
      badCallback: 'El enlace de retorno debe ser una dirección https.',
      badRequest: 'Este enlace de pago no es una solicitud de Bitcoin válida.',
      deny: 'No compartir',
      errorTitle: 'Solicitud de la app',
      goHome: 'Ir a la wallet',
      missingCallback: 'Este enlace no tiene a dónde volver.',
      missingRequest: 'A este enlace de pago le falta la solicitud.',
      open: 'Abrir',
      openInApp: 'Abrir en la app',
      openInAppHint: 'Termina esta solicitud en la app de Arkade instalada.',
      openInAppSafari:
        'Safari no puede abrir la app instalada desde esta página. Abre Arkade desde la pantalla de inicio, o termina la solicitud aquí.',
      pubkey: 'Clave pública',
      returnTo: 'Volver a {host}',
      sentReturn: '{amount} enviado. Toca para volver a {host}',
      share: 'Compartir dirección',
      shareExplainer: 'Esto comparte tu dirección de recepción y tu clave pública. No puede gastar tus fondos.',
      title: 'Conectar',
      tooLong: 'Este enlace es demasiado largo.',
      unknownAction: 'Este enlace no es una solicitud conocida.',
      wantsAddress: '{host} pide tu dirección de Arkade.',
    },
    apps: {
      dfx: 'DFX',
      dfxConnectionFailed: 'No se pudo conectar con DFX. Vuelve atrás e inténtalo de nuevo.',
    },
    backup: {
      backupFailed: 'El respaldo a Nostr falló',
      cantRecover: 'Si lo pierdes no podrás recuperarlo',
      confirm: 'Confirmar',
      copyToClipboard: 'Copiar al portapapeles',
      doNotShare: 'No lo compartas con nadie',
      enableNostrBackups: 'Activar respaldos Nostr',
      enoughToRestore: 'Esto es suficiente para restaurar tu wallet.',
      forYourEyesOnly: 'Solo para tus ojos, no lo compartas.',
      keepSafe: 'Mantén tu {secret} a salvo',
      keyBacksUpWallet:
        'Tu clave privada se usa para respaldar tu wallet. Mántela en secreto y a salvo en todo momento.',
      nostrBackupUpdated: 'Respaldo de Nostr actualizado',
      nostrBackupsSubtext: 'Activar o desactivar respaldos Nostr',
      phraseBacksUpWallet:
        'Tu frase de recuperación se usa para respaldar tu wallet. Mántela en secreto y a salvo en todo momento.',
      phraseWarning:
        'Tu frase de recuperación puede usarse para acceder a todo en tu wallet. No la compartas con nadie.',
      privateKey: 'Clave privada',
      privateKeyWarning: 'Tu clave privada puede usarse para acceder a todo en tu wallet. No la compartas con nadie.',
      recoveryPhrase: 'Frase de recuperación',
      viewPrivateKey: 'Ver clave privada',
      viewRecoveryPhrase: 'Ver frase de recuperación',
    },
    browser: {
      copied: 'Copiado',
      copyLink: 'Copiar enlace',
      notSupported: 'No funciona en este navegador.',
      stepBrowser: 'Abre Safari, Chrome o tu navegador',
      stepCopy: 'Copia el enlace de abajo',
      stepPaste: 'Pega el enlace y ve',
      welcome: 'Bienvenido a Arkade 👾',
    },
    common: {
      address: 'Dirección',
      all: 'Todo',
      amount: 'Monto',
      back: 'Atrás',
      balance: 'Saldo',
      cancel: 'Cancelar',
      close: 'Cerrar',
      continue: 'Continuar',
      copied: '¡Copiado!',
      copiedToClipboard: 'Copiado al portapapeles',
      dismiss: 'Descartar',
      failedToCopy: 'No se pudo copiar',
      generateOrImportCoins: 'Genera o importa monedas para continuar',
      gotIt: 'Entendido',
      home: 'Inicio',
      importOrMintToStart: 'Importa o acuña uno para comenzar',
      learnMore: 'Aprender más',
      loading: 'Cargando...',
      makeTransactionToStart: 'Haz una transacción para comenzar.',
      network: 'Red',
      noAssetsYet: 'Aún no hay activos',
      noLogsAvailable: 'No hay registros disponibles',
      noTransactionsYet: 'Aún no hay transacciones',
      noVirtualCoins: 'No hay monedas virtuales disponibles',
      off: 'Desactivado',
      on: 'Activado',
      paste: 'Pegar',
      sats: 'sats',
      save: 'Guardar',
      shortly: 'en breve',
      startUsingToGenerateLogs: 'Comienza a usar la app para generar registros.',
      success: 'Éxito',
      total: 'Total',
      tryAgain: 'Reintentar',
      unknown: 'Desconocido',
      value: 'Valor',
    },
    components: {
      arkadeAddress: 'Dirección Arkade',
      btcAddress: 'Dirección BTC',
      cameraBlocked:
        'El acceso a la cámara está bloqueado. Permítelo para este sitio en la configuración de tu navegador y vuelve a intentarlo.',
      cameraNotAvailable: 'Cámara no disponible',
      clear: 'Limpiar',
      clearAmount: 'Borrar monto',
      confirmPassword: 'Confirmar contraseña',
      copyAddress: 'Copiar dirección',
      filterActivity: 'Filtrar actividad',
      focusableElement: 'Elemento enfocable',
      hideBalances: 'Ocultar saldos',
      lightningInvoice: 'Factura Lightning',
      lnurlAddress: 'LNURL',
      noPasswordYolo: 'Sin contraseña, a la de perder',
      password: 'Contraseña',
      passwordMinChars: 'mínimo 8 caracteres',
      passwordOneNumber: 'contener al menos 1 número',
      passwordSpecialChar: 'contener al menos 1 carácter especial',
      passwordsMustMatch: 'Las contraseñas deben coincidir',
      protectWalletWithPassword: 'Protege tu wallet con una contraseña',
      recoveryPhraseOrKey: 'Frase de recuperación o clave privada',
      reload: 'Recargar',
      reloadToContinue: 'Recarga para continuar',
      save: 'Guardar',
      savePassword: 'Guardar contraseña',
      scanQr: 'Escanear QR',
      setMaximumAmount: 'Establecer monto máximo',
      showBalances: 'Mostrar saldos',
      somethingWentWrong: 'Algo salió mal',
      strongPasswordWith: 'Establece una contraseña segura con:',
      toggleCurrency: 'Cambiar moneda',
      toggleVisibility: 'Cambiar visibilidad',
      unexpectedError: 'La app encontró un error inesperado',
    },
    contracts: {
      active: 'Activo',
      deprecatedSigner: 'firmante obsoleto',
      deprecatedSignerCutoff: 'firmante obsoleto · fecha límite superada',
      inactive: 'Inactivo',
      noContracts: 'No se encontraron contratos.',
      parameters: 'Parámetros',
      pastRefundLocktime: 'Tiempo de reembolso pasado',
      refundLocktime: 'Tiempo de reembolso',
      script: 'Script',
      searchPlaceholder: 'Buscar tipo, dirección, script…',
    },
    delegate: {
      active: 'Activo',
      addressLabel: 'dirección: {value}',
      delegateDescription:
        'Un delegado es un tercero de confianza que nombras para ayudar a mantener tus VTXOs seguros.',
      delegatesRenewOnly:
        'Los delegados solo pueden renovar tus VTXOs, no pueden gastar tus fondos ni controlar tu wallet',
      feeLabel: 'fee: {value}',
      inactive: 'Inactivo',
      nextRenewal: 'próxima renovación {time}',
      noDelegateFound: 'No se encontró ningún delegado para esta red.',
      noUpcomingRenewal: 'No hay renovaciones próximas',
      pubkeyLabel: 'pubkey: {value}',
      useDefaultArkadeDelegate: 'Usar delegado por defecto de Arkade',
      useDefaultSubtext: 'Usa el delegado por defecto de Arkade para gestionar renovaciones',
      walletReloadToApply: 'La wallet se recargará para aplicar el cambio.',
      whatIsADelegate: '¿Qué es un delegado?',
    },
    errors: {
      amountBetween: 'El monto debe estar entre {min} y {max} sats',
      outdatedWallet:
        'Tu wallet está desactualizada y necesita actualizarse para ser compatible con la última versión de Arkade.',
      recentlySettledHours: 'Tus fondos se liquidaron recientemente en la cadena — inténtalo de nuevo en unas horas',
      recentlySettledRetry: 'Tus fondos se liquidaron recientemente en la cadena — inténtalo de nuevo {when}',
    },
    formatting: {
      ago: 'hace {value}',
      ahead: 'en {value}',
      day: 'día',
      days: 'días',
      hour: 'hora',
      hours: 'horas',
      justNow: 'ahora mismo',
      minute: 'minuto',
      minutes: 'minutos',
      second: 'segundo',
      seconds: 'segundos',
      unit: '{count} {unit}',
    },
    init: {
      addressRotation: 'Rotación de direcciones',
      arkadeServerUnreachable: 'Servidor de Arkade inaccesible',
      biometricsDescription:
        'Esto te permitirá iniciar sesión fácilmente con datos biométricos sin necesidad de recordar la contraseña.',
      connectingToServer: 'Conectando al servidor',
      connectingToServerStatus: 'Conectando al servidor',
      connectionFailed: 'Conexión fallida',
      createNewWallet: 'Crear nueva wallet',
      createPasskey: 'Crear passkey',
      createReadyText: 'Tu wallet se ha creado correctamente y ya está lista para usar.',
      createWallet: 'Crear wallet',
      goToWallet: 'Ir a la wallet',
      newWalletLive: '¡Tu nueva wallet está lista!',
      otherLoginOptions: 'Otras opciones de acceso',
      recoveryChecksumError: 'La frase de recuperación no es válida. Revisa las palabras y su orden.',
      recoveryInputHint: 'Escribe o pega tu frase de recuperación o clave privada.',
      recoveryInstructions:
        'Introduce tu frase de recuperación de 12 palabras, o una clave privada que empiece con "nsec" o una clave hexadecimal. No la compartas con nadie.',
      recoveryKeyError: 'Introduce una clave privada nsec válida o una clave privada hexadecimal de 64 caracteres.',
      recoveryOneWord: '1 palabra introducida',
      recoveryWordCount: '{count} palabras introducidas',
      recoveryWordCountError: 'Introduce la frase de recuperación completa: 12, 15, 18, 21 o 24 palabras.',
      recoveryWordError: 'No se reconoce la palabra {number}. Revisa su escritura.',
      restoreReadyText: 'Tu wallet se ha restaurado correctamente y ya está lista para usar.',
      restoreSuccessText: 'Tu wallet, configuraciones y swaps se han restaurado correctamente.',
      restoreWallet: 'Restaurar wallet',
      restoringWallet: 'Restaurando wallet...',
      rotateReceiveAddresses: 'Rotar direcciones de recepción',
      rotateReceiveSubtext:
        'Deriva una dirección nueva para cada pago entrante (wallet HD). Mejora la privacidad on-chain. Recomendado con el respaldo Nostr activado para que las direcciones rotadas se puedan recuperar al restaurar. Para usuarios avanzados.',
      rotationSubtext:
        'Heredar usa tu configuración guardada de wallet (normalmente restaurada del respaldo). Si el respaldo no está disponible, se usa tu configuración local/por defecto. Elige HD si esta wallet rotó direcciones de recepción y necesitas forzar la recuperación HD.',
      tagline1: 'Pagos rápidos, swaps y más',
      tagline2: 'Accede a Lightning, acuña activos y más. Todo protegido por Bitcoin',
      tagline3: 'Tú controlas. Liquida y retira cuando quieras',
      useBiometrics: 'Usar datos biométricos',
      usePassword: 'Usar contraseña',
      walletRestored: '¡Wallet restaurada exitosamente!',
      wordSuggestions: 'Sugerencias de palabras',
    },
    loading: {
      burning: 'Quemando...',
      connectingToDfx: 'Conectando a DFX...',
      connectingToServiceWorker: 'Conectando al service worker...',
      fetchAssetDetails: 'Obteniendo detalles del activo...',
      fetchingCoins: 'Obteniendo monedas...',
      fetchingTransactions: 'Obteniendo transacciones...',
      loadingAsset: 'Cargando activo...',
      loadingAssetMetadata: 'Cargando metadatos de activos...',
      loadingAssets: 'Cargando activos...',
      loadingWalletData: 'Cargando datos de la wallet...',
      mintingAsset: 'Acuñando activo...',
      mintingControlAsset: 'Acuñando activo de control...',
      payingToMainnet: 'Pagando a la red principal. Esto puede tardar unos momentos.',
      recoveringAddresses: 'Recuperando direcciones...',
      reissuing: 'Reemitiendo...',
      renewing: 'Renovando tus monedas virtuales. Esto puede tardar unos momentos.',
      retryingConnection: 'Reintentando conexión...',
      settlingTransactions: 'Liquidando transacciones. Esto puede tardar unos momentos.',
      startingWallet: 'Iniciando wallet...',
      updatingBalance: 'Actualizando saldo...',
    },
    logs: {
      logAt: 'Registro de {time} con el mensaje {msg}. Presiona Escape para salir de la navegación por teclado.',
      pressEnter: 'Al presionar Enter se activa la navegación por teclado de los registros',
    },
    mint: {
      additionalAmount: 'Monto adicional',
      amount: 'Monto *',
      amountMustBePositive: 'El monto debe ser un número positivo',
      amountToBurn: 'Monto a quemar',
      arkadeMint: 'Arkade Mint',
      arkadeMintDisabled: 'Arkade Mint está desactivado.',
      assetCreated: 'Activo creado',
      assetId: 'ID del activo',
      assetIdMustBeHex: 'El ID del activo debe ser una cadena hexadecimal de 68 caracteres',
      assetIdRequired: 'El ID del activo es obligatorio',
      assetIdTapToCopy: 'ID del activo (toca para copiar)',
      assetMinted: '¡Activo acuñado!',
      assetNotFound: 'Activo no encontrado',
      backToArkadeMint: 'Volver a Arkade Mint',
      burn: 'Quemar',
      burnTitle: 'Quemar {ticker}',
      burnWarning: 'Estás a punto de quemar {amount} {ticker}. Esta acción es irreversible.',
      confirmBurn: 'Confirmar quema',
      confirmReissue: 'Confirmar reemisión',
      controlAmount: 'Monto de control',
      controlAmountPositive: 'El monto de control debe ser positivo',
      controlAsset: 'Activo de control',
      decimals: 'Decimales',
      decimalsRange: 'Los decimales deben estar entre 0 y {max}',
      enableArkadeMint: 'Activar Arkade Mint',
      enableArkadeMintSubtext: 'Activar o desactivar Arkade Mint',
      enableIt: 'Actívalo',
      enterAmount: 'Ingresa un monto',
      enterControlAmount: 'Ingresa el monto del activo de control',
      enterName: 'Ingresa un nombre',
      enterTicker: 'Ingresa un ticker',
      exceedsBalance: 'No puedes quemar más de tu saldo ({balance} {ticker})',
      existing: 'Existente',
      hideIcon: 'Ocultar icono',
      iconUrl: 'URL del icono',
      import: 'Importar',
      importAsset: 'Importar activo',
      invalidDecimals: 'Los decimales deben ser un entero entre 0 y {max}',
      invalidIconUrl: 'URL del icono no válida',
      max: 'Max',
      mint: 'Acuñar',
      myToken: 'Mi Token',
      name: 'Nombre *',
      nameLabel: 'Nombre',
      nameTooLong: 'El nombre debe tener 40 caracteres o menos',
      new: 'Nuevo',
      none: 'Ninguno',
      pasteAssetId: 'Pegar ID del activo...',
      receive: 'Recibir',
      reissue: 'Reemitir',
      reissueTitle: 'Reemitir {ticker}',
      reissueWarning: 'Estás a punto de acuñar {amount} adicional de {ticker}.',
      remove: 'Eliminar',
      selectFromWallet: 'Selecciona de la wallet...',
      send: 'Enviar',
      settingsTitle: 'Configuración de Arkade Mint',
      showIcon: 'Mostrar icono',
      supply: 'Suministro',
      ticker: 'Ticker *',
      tickerLabel: 'Ticker',
      tickerTooLong: 'El ticker debe tener 8 caracteres o menos',
      title: 'Acuñar activo',
      toViewYourAssets: 'para ver tus activos.',
      unknown: 'Desconocido',
      unknownAsset: 'Activo desconocido',
      viewAsset: 'Ver activo',
    },
    notes: {
      arkNote: 'Nota Ark',
      noteRedeemed: '¡Nota canjeada!',
      processing: 'Procesando. Esto puede tardar unos momentos.',
      redeemNote: 'Canjear nota',
      redeemedSuccessfully: 'canjeada correctamente',
      redeeming: 'Canjeando...',
      soundsGood: '¡Perfecto!',
      title: 'Nota',
    },
    notifications: {
      apiNotSupported: 'La API de notificaciones no está soportada',
      permissionDenied: 'Permiso de notificaciones denegado',
    },
    receive: {
      addAmount: 'Añadir monto',
      addName: 'Añadir un nombre',
      amount: 'Monto',
      arkadeAddress: 'Dirección Arkade',
      backToMethod: 'Volver a {method}',
      bitcoinAddress: 'Dirección Bitcoin',
      chooseName: 'Elige un nombre',
      chooseNameOptional: 'Elige un nombre (opcional)',
      claimCode: 'Código de reclamo',
      claimCodeInvalid: 'Ese código de reclamo no es válido.',
      claimCodeNeedsName: 'Este código es para un nombre que tú eliges. Añade uno y vuelve a reclamar.',
      claimCodeOptional: 'Código de reclamo (opcional)',
      claimFailed: 'Fallo al cobrar el pago Lightning: {error}',
      claimModeUnavailable: 'Este servidor no ofrece esa opción.',
      claimName: 'Reclamar nombre',
      claimNotAllowed: 'Ese nombre no está permitido aquí.',
      claimRateLimited: 'Demasiados intentos; inténtalo de nuevo en un momento.',
      claimTaken: 'Ese nombre ya está en uso.',
      clearAmount: 'Borrar monto',
      copy: 'Copiar',
      copyAddress: 'Copiar dirección',
      copyAria: 'Copiar {title}',
      copyQrCode: 'Copiar código QR',
      editAmount: 'Editar monto',
      failedToGetAddress: 'Error al obtener la dirección: {error}',
      generatingInvoice: 'Generando factura…',
      getNewQuote: 'Obtener una nueva cotización',
      gettingQuote: 'Obteniendo cotización…',
      haveClaimCode: 'Tengo un código de reclamo',
      invalidNote: 'Nota inválida',
      lightningInvoice: 'Factura Lightning',
      lightningPaymentLost: 'Pago Lightning perdido: el solver lo reclamó antes de poder cobrarse',
      lightningUnavailable: 'Lightning no disponible: {error}',
      lnaddressClaimFailed: 'No se pudo obtener una dirección Lightning.',
      lnaddressUnavailable: 'Dirección Lightning no disponible: {error}',
      methodArkade: 'Arkade',
      methodBitcoin: 'Bitcoin',
      methodLightning: 'Lightning',
      noInvoiceForAmount: 'Sin factura para este monto: {error}',
      noLightningSolver: 'No hay solucionador Lightning disponible',
      noPaymentMethods: 'No hay métodos de pago válidos para este monto',
      noTokenQuote: 'Sin cotización para este token: {error}',
      openInWallet: 'Abrir en la billetera',
      payWithStablecoins: 'Pagar con stablecoins',
      pickForMe: 'Elige uno por mí',
      quoteExpired: 'Cotización vencida',
      quoteExpiredSub: 'Esa dirección de depósito ya no es válida. No envíes a ella.',
      requestingAmount: 'Solicitando {amount} {unit}',
      setAmount: 'Establecer monto',
      share: 'Compartir',
      tokenAmount: 'Monto exacto ({unit})',
      tokenDepositAddress: 'Dirección de depósito {token}',
      tokenQuoteValid: 'Cotización de {provider}, válida por {time}',
      tokenSendExactly: 'Envía exactamente {amount} {unit} en {chain}',
      unified: 'Unificado',
    },
    scanner: {
      cameraError: 'Cámara no disponible',
      tryAgain: 'Reintentar',
    },
    send: {
      amountAboveLnurlMax: 'Monto por encima del máximo LNURL',
      amountAboveMax: 'Monto por encima del máximo',
      amountBelowLnurlMin: 'Monto por debajo del mínimo LNURL',
      amountBelowMin: 'Monto por debajo del mínimo',
      amountBelowOneSat: 'Monto por debajo de 1 satoshi',
      amountTooLow: 'Monto demasiado bajo',
      amountToSend: 'Monto a enviar',
      arkadeServerKeyMismatch: 'La clave del servidor Arkade no coincide',
      asset: 'Activo',
      assetsOnlyToArkade: 'Los activos solo pueden enviarse a direcciones Arkade',
      available: '{amount} disponible',
      balanceReserve: 'Reserva de saldo',
      balanceReserveText:
        '{dust} sats se mantienen en reserva para proteger tus activos. Tu monto máximo de envío es {max} sats.',
      bitcoin: 'Bitcoin',
      continue: 'Continuar',
      directionSendingAssets: 'Enviando activos',
      errorSendingTransaction: 'Error al enviar la transacción',
      feeChanged: 'La comisión de red cambió. Revisa el nuevo total y vuelve a tocar para firmar.',
      feesDeductedFromAmount: 'Las comisiones se deducirán del monto enviado',
      gettingQuote: 'Obteniendo cotización',
      insufficientAssetBalance: 'Saldo de activo insuficiente',
      insufficientFunds: 'Fondos insuficientes',
      insufficientFundsDetail: 'Fondos insuficientes, solo tienes {balance} sats',
      insufficientFundsForLnurl: 'Fondos insuficientes para LNURL',
      insufficientFundsMax: 'Fondos insuficientes. Tras las comisiones de red puedes enviar hasta {max} sats',
      invalidAmount: 'Monto inválido',
      invalidArkadeAddressFromLnurl: 'Dirección Arkade inválida recibida de LNURL',
      invalidRecipient: 'Dirección de destinatario inválida',
      invoiceMustHaveAmount: 'La factura debe tener un monto definido',
      lnurlNotFound: 'LNURL no encontrado',
      missingAddress: 'Falta la dirección',
      missingAmount: 'Falta el monto',
      missingAssetAmount: 'Falta el monto del activo',
      missingSatoshisAmount: 'Falta el monto en satoshis',
      missingTotalAmount: 'Falta el monto total',
      onTheWay: '{amount} en camino',
      onTheWayKeepOpen: '{amount} en camino — mantén la wallet abierta hasta que se complete',
      payingInsideArkade: 'Pagando dentro de Arkade',
      payingToLightning: 'Pagando a Lightning',
      payingToMainnet: 'Pagando a mainnet',
      paymentOnTheWay: 'El pago está en camino',
      paymentSent: 'Pago enviado',
      pendingOnchain: 'Un pago onchain sigue en camino — mantén la wallet abierta hasta que se complete',
      quoteForDifferentInvoice: 'La cotización es para otra factura',
      quoteForDifferentPayment: 'La cotización es para otro pago',
      recipientAddress: 'Dirección del destinatario',
      sendMax: 'Enviar máximo',
      sendingOffchainNotAllowed: 'El envío offchain no está permitido',
      sendingOnchainNotAllowed: 'El envío onchain no está permitido',
      sentSuccessfully: '{amount} enviado correctamente',
      serverUnreachable: 'Servidor inaccesible',
      signTransaction: 'Firmar transacción',
      tapToSign: 'Toca para firmar',
      title: 'Enviar',
      unableToDecodeInvoice: 'No se pudo decodificar la factura',
      unableToGetReceivingAddresses: 'No se pudieron obtener las direcciones de recepción',
      unableToParseBip21: 'No se pudo analizar bip21',
      unknownAsset: 'Activo desconocido',
      verifiedByBranta: 'Verificado por Branta',
      verifyingAddress: 'Verificando dirección...',
    },
    settings: {
      about: 'Acerca de',
      advanced: 'Avanzado',
      allowNotifications: 'Permitir notificaciones',
      appearance: 'Apariencia',
      arkadeMint: 'Arkade Mint',
      auto: 'Auto',
      backup: 'Respaldo',
      bitcoinUnit: 'Unidad de bitcoin',
      boardingExitDelay: 'Retraso de salida de boarding',
      bugReports: 'Reportes de errores',
      bugReportsText:
        'Reporta cualquier problema o error que encuentres. Incluye pasos para reproducirlo para una resolución más rápida.',
      cannotUndo: 'Esta operación no se puede deshacer.',
      changePassword: 'Cambiar contraseña',
      clear: 'Limpiar',
      connectToServer: 'Conectar al servidor',
      contracts: 'Contratos',
      currency: 'Moneda',
      customerSupport: 'Atención al cliente',
      customerSupportText:
        'Obtén ayuda con tu wallet, reporta errores o haz preguntas. Nuestro equipo de soporte está aquí para ayudarte.',
      dark: 'Oscuro',
      delegates: 'Delegados',
      didYouBackup: '¿Has hecho un respaldo de tu wallet?',
      display: 'Pantalla',
      dust: 'Dust',
      english: 'Inglés',
      exportToCsv: 'Exportar a archivo CSV',
      failedToLoadSupportChat: 'Error al cargar el chat de soporte',
      failedToUpdatePassword: 'Error al actualizar la contraseña',
      feedback: 'Retroalimentación',
      forfeitAddress: 'Dirección de forfeit',
      general: 'General',
      gitCommitHash: 'Hash de commit git',
      hapticFeedback: 'Vibración táctil',
      hapticSubtext: 'Vibración al tocar botones e interactuar',
      iHaveBackedUp: 'He hecho un respaldo de mi wallet',
      language: 'Idioma',
      light: 'Claro',
      lightningAddress: 'Dirección Lightning',
      lnurlAddName: 'Añadir un nombre',
      lnurlLimits: 'Lo que pueden enviar los pagadores',
      lnurlNone: 'Aún no tienes dirección Lightning.',
      lnurlPrimary: 'Dirección principal',
      lnurlServer: 'Servidor',
      lnurlYours: 'Tus direcciones Lightning',
      lock: 'Bloqueo',
      lockExplanation: 'Después de bloquear tendrás que introducir tu contraseña para desbloquear.',
      lockWallet: 'Bloquear wallet',
      lockYourWallet: 'Bloquear tu wallet',
      logs: 'Registros',
      noPasswordDefined: 'Sin contraseña definida',
      notes: 'Notas',
      notifications: 'Notificaciones',
      notificationsIntro:
        'Recibe una notificación cuando haya una actualización o llegue un pago. Se pedirá tu permiso si es necesario.',
      notificationsUnsupported:
        'Tu navegador no soporta la API de Notificaciones. En iOS necesitas "Añadir a pantalla de inicio" y tener iOS 16.4 o superior.',
      openSupportChat: 'Abrir chat de soporte',
      passwordChanged: 'Contraseña cambiada',
      passwordChangedToBiometrics: 'Contraseña cambiada a datos biométricos',
      passwordRemoved: 'Contraseña eliminada',
      portuguese: 'Portugués',
      preferences: 'Preferencias',
      receive: 'Recibir',
      receiveViaTokens: 'Aceptar tokens',
      receiveViaTokensSubtext:
        'Con un monto, quien te paga también puede enviar {units}, que un tercero convierte a BTC en Arkade.',
      resetFailed:
        'No se pudo restablecer la wallet. Es posible que se hayan borrado algunos datos; cierra otras pestañas e inténtalo de nuevo.',
      resetWallet: 'Restablecer wallet',
      sameServer: 'Mismo servidor',
      secureChat: 'Chat seguro',
      secureChatText: 'Tus conversaciones son seguras y privadas. El historial se mantiene entre sesiones.',
      security: 'Seguridad',
      server: 'Servidor',
      serverFound: 'Servidor encontrado',
      serverPubkey: 'Pubkey del servidor',
      serverResetWarning: 'Tu wallet se restablecerá. Asegúrate de hacer un respaldo antes.',
      serverUnavailableOnMainnet: 'Los ajustes del servidor no están disponibles en mainnet.',
      serverUrl: 'URL del servidor',
      sessionDuration: 'Duración de sesión',
      setPassword: 'Establecer contraseña',
      setPasswordToLock: 'Necesitas establecer una contraseña para bloquear.',
      solvers: 'Solvers',
      spanish: 'Español',
      success: 'Éxito',
      support: 'Soporte',
      supportChatNotConfigured: 'El chat de soporte no está configurado',
      theme: 'Tema',
      title: 'Ajustes',
      tokenExplainer:
        'Quien te paga envía tokens a {provider}, un tercero que guarda el depósito mientras paga una factura Lightning. Luego, un swap de intent-solver te entrega ese pago como BTC en Arkade. Un depósito tardío, insuficiente o excesivo se resuelve con {provider}.',
      tokenExplainerConfirm: 'Activar',
      tokenExplainerTitle: 'Antes de aceptar tokens',
      trackProgress: 'Seguimiento',
      trackProgressText:
        'Todos tus tickets y conversaciones se guardan. Puedes ver conversaciones pasadas en cualquier momento.',
      unableToConnect: 'No se pudo conectar',
      unilateralExitDelay: 'Retraso de salida unilateral',
      useBiometrics: 'Usar datos biométricos',
      vtxos: 'Monedas virtuales',
      walletMode: 'Modo de wallet',
    },
    solvers: {
      addNew: '+ Añadir nueva',
      buildShipsPlural: 'Este build incluye {count} tarjetas de solver; añade las tuyas para llegar a más solvers.',
      buildShipsSingular: 'Este build incluye {count} tarjeta de solver; añade las tuyas para llegar a más solvers.',
      builtIn: 'Integrado',
      builtInPlusOwnPlural: '{builtIn} tarjetas de solver integradas, más {own} propias.',
      builtInPlusOwnSingular: '{builtIn} tarjeta de solver integrada, más {own} propias.',
      cardInfo: 'Información de la tarjeta del solver',
      confirmRemove: 'Confirmar eliminación',
      currentLabel: 'Actual',
      edit: 'Editar',
      failedRemove: 'Error al eliminar la tarjeta: el almacenamiento está lleno o no disponible.',
      failedSave: 'Error al guardar la tarjeta: el almacenamiento está lleno o no disponible.',
      fieldBase: 'Base',
      fieldDiscoveryKey: 'Clave de descubrimiento',
      fieldFee: 'Comisión',
      fieldName: 'Nombre',
      fieldQuote: 'Cotización',
      fieldVersion: 'Versión',
      hideRaw: 'Ocultar JSON',
      invalidCard: 'tarjeta inválida: {message}',
      invalidJson: 'JSON inválido: {message}',
      marketsLabel: 'Mercados',
      noStoredCards: 'No tienes tarjetas de solver guardadas en tu wallet.',
      originalLabel: 'Original',
      publishedMarkets: 'Mercados publicados por el solver',
      remoteChecking: 'Buscando la tarjeta actual…',
      remoteError: 'No se pudo cargar la tarjeta actual.',
      remoteNoRelays: 'Solo local — esta tarjeta no tiene relays Nostr.',
      remoteNotFound: 'No se encontró una tarjeta actual en sus relays.',
      remove: 'Eliminar',
      removeWarning: '¿Seguro que quieres eliminar la tarjeta "{label}"? Esta acción no se puede deshacer.',
      storedPlural: 'Tienes {count} tarjetas de solver guardadas en tu wallet.',
      storedSingular: 'Tienes {count} tarjeta de solver guardada en tu wallet.',
      viewInfo: 'Ver info',
      viewRaw: 'Ver JSON',
    },
    swap: {
      amountTooSmall: 'Monto demasiado pequeño',
      asset: 'activo',
      assetAmount: 'monto del activo',
      chooseAsset: 'Elige activo',
      chooseAssetToReceive: 'Elige el activo a recibir',
      chooseAssetToSwap: 'Elige el activo a intercambiar',
      comingSoon: 'Los swaps llegan pronto',
      comingSoonText: 'Estamos puliendo la experiencia de swap antes de activarla aquí.',
      confirmSwap: 'Confirmar swap',
      createdTapHome: 'Swap creado. Toca para ir al inicio.',
      currencyAmount: 'monto de {currency}',
      deleteDigit: 'Borrar dígito',
      fees: 'Comisiones',
      insufficientBalance: 'Saldo insuficiente',
      keypadFor: 'Teclado de swap para {amount}',
      max: 'Máx',
      maximum: 'Máximo',
      minimum: 'Mínimo',
      noAssetsMatch: 'Ningún activo coincide con esta búsqueda',
      pairUnavailable: 'Swap no disponible para este par',
      partialSwapNeedsBtc:
        'No tienes suficiente bitcoin para hacer un intercambio parcial. Intercambia todo o consigue algo de bitcoin.',
      pending: 'Pendiente',
      pickAssetForSide: 'Elige el activo para este lado del swap.',
      quoteUnavailable: 'Cotización no disponible',
      receive: 'Recibir',
      reviewSwap: 'Revisar swap',
      reviewSwapDescription: 'Comprueba la ruta y los totales estimados.',
      routeConnector: 'a',
      searchAssets: 'Buscar activos',
      selectAssetToTradeFrom: 'Selecciona el activo desde el que quieres operar.',
      showFirst: 'Mostrar {mode} primero',
      swapCreated: 'Swap creado',
      swapRoute: 'Ruta del swap',
      swapsUnavailable: 'Los swaps no están disponibles',
      swapsUnavailableText: 'Añade otro activo compatible para intercambiar entre saldos.',
      switchSwapDirection: 'Cambiar dirección del swap',
      title: 'Intercambio',
      useMaximum: 'Usar máximo {balance}',
      waitingForFill: 'Esperando ejecución',
    },
    table: {
      keyboardNavIntro: 'Presionar Enter habilita la navegación con teclado de la tabla',
      keyboardNavTitle: 'Título {title} con estado {value}. Presiona Escape para salir de la navegación con teclado.',
    },
    transaction: {
      addReminder: 'Añadir recordatorio',
      amountBurned: 'Monto quemado',
      amountExited: 'Monto salido',
      amountIssued: 'Monto emitido',
      amountReceived: 'Monto recibido',
      amountReissued: 'Monto reemitido',
      amountSent: 'Monto enviado',
      assetId: 'ID del activo',
      assetIdTicker: 'ID del activo ({ticker})',
      assetIdTickerUnverified: 'ID del activo ({ticker}, sin verificar)',
      assetIdUnverified: 'ID del activo (sin verificar)',
      boardingExpired: 'La transacción de boarding expiró.',
      burn: 'Quemado',
      cancelSwap: 'Cancelar swap',
      cancelSwapBody: 'Si el swap sigue pendiente, esto devolverá sus fondos bloqueados a tu wallet.',
      cancelSwapTitle: '¿Cancelar el swap?',
      cancelled: 'Cancelada',
      cancelling: 'Cancelando…',
      completeBoarding: 'Completar boarding',
      completed: 'Completada',
      exited: 'Salida',
      expired: 'Expirada',
      failed: 'Fallida',
      fromAssetId: 'ID del activo de origen',
      fromAssetIdUnverified: 'ID del activo de origen (sin verificar)',
      issuance: 'Emisión',
      keepSwap: 'Mantener swap',
      keyboardNavAria:
        'Transacción {type} por monto de {amount}. Presiona Escape para salir de la navegación con teclado.',
      keyboardNavHint: 'Presionar Enter habilita la navegación con teclado de la lista de transacciones',
      lightningReceive: 'Recepción Lightning',
      lightningReceiveFailed: 'Recepción Lightning fallida',
      lightningReceiveLost: 'Recepción Lightning perdida',
      lightningReceivePending: 'Recepción Lightning pendiente',
      lightningReceiveRefunded: 'Recepción Lightning reembolsada',
      lightningSend: 'Envío Lightning',
      lightningSendFailed: 'Envío Lightning fallido',
      lightningSendLost: 'Envío Lightning perdido',
      lightningSendPending: 'Envío Lightning pendiente',
      lightningSendRefunded: 'Envío Lightning reembolsado',
      lnurlReceivedAt: 'Recibido en {address}',
      lnurlReceivedVia: 'Recibido vía {rail}',
      lnurlSentTo: 'Enviado a {address}',
      lnurlSentVia: 'Enviado vía {rail}',
      paymentReceived: 'Pago recibido',
      paymentReceivedAria: 'Pago recibido correctamente. Toca para ir a la pantalla de inicio.',
      pending: 'Pendiente',
      pendingBoarding: 'Boarding pendiente',
      pendingBoardingText: 'Transacción de boarding confirmada on-chain.',
      preconfirmed: 'Preconfirmada',
      received: 'Recibido',
      receivedSuccessfully: 'recibido correctamente',
      receiverConfirmed: 'Receptor confirmado',
      receiverConfirmedText: 'El servidor del receptor confirmó que este pago se liquidó.',
      receiverNotConfirmed: 'Sin confirmar',
      receiverNotConfirmedText: 'El receptor no ha confirmado que este pago se liquidó.',
      recoverInProgress: 'La recuperación sigue en curso. Vuelve a comprobarlo en unos minutos.',
      recoverNotIncluded: 'La ronda de recuperación aún no incluyó este depósito. Inténtalo de nuevo en unos minutos.',
      recoverNothing: 'No queda nada por recuperar en este swap.',
      recoverSwap: 'Recuperar fondos',
      recoverSwapBody:
        'Esto liquida el depósito expirado de vuelta a tu wallet en la próxima ronda, junto con cualquier otra moneda expirada. Puede tardar unos minutos.',
      recoverSwapTitle: '¿Recuperar los fondos del swap?',
      recoverUnsupported: 'Esta wallet no puede ejecutar una ronda de recuperación.',
      recoverable: 'Recuperable',
      recoverableText:
        'El depósito expiró antes de que se completara el swap. Tus fondos están a salvo: recupéralos para volver a gastarlos.',
      recovering: 'Recuperando…',
      refunded: 'Reembolsada',
      renew: 'Renovar',
      retryCancel: 'Reintentar cancelación',
      retryRecover: 'Reintentar recuperación',
      sent: 'Enviado',
      settleTransaction: 'Liquidar transacción',
      settled: 'Liquidada',
      settledSuccessfully: 'Transacción liquidada correctamente',
      settling: 'Liquidando...',
      soundsGood: '¡Perfecto!',
      success: 'Éxito',
      swap: 'Swap',
      swapCancelled: 'Swap cancelado',
      swapFailed: 'Swap fallido',
      swapPending: 'Swap pendiente',
      swapRecoverable: 'Swap recuperable',
      toAssetId: 'ID del activo de destino',
      toAssetIdUnverified: 'ID del activo de destino (sin verificar)',
      totalReceived: 'Total recibido',
      totalTicker: 'Total ({ticker})',
      unconfirmed: 'Sin confirmar',
      unconfirmedText: 'Transacción on-chain sin confirmar. Espera la confirmación.',
    },
    unlock: {
      connectionFailed: 'Error de conexión. Inténtalo de nuevo.',
      enterPassword: 'Introduce tu contraseña',
      insertPassword: 'Introducir contraseña',
      invalidPassword: 'Contraseña incorrecta',
      passwordPlaceholder: 'contraseña',
      title: 'Desbloquear',
      unlockWallet: 'Desbloquear wallet',
      unlockWithPasskey: 'Desbloquear con tu passkey',
    },
    upsells: {
      buySellDescription: 'Usa una transferencia bancaria en CHF o EUR vía SEPA.',
      buySellTitle: 'Comprar o vender bitcoin',
      title: 'Haz más con tu dinero',
    },
    vtxos: {
      automaticRenewal: 'La renovación automática ocurre para monedas que expiran dentro de {time}.',
      belowDustLimit: 'Por debajo del límite de dust',
      boardingUtxos: 'Tus utxos de boarding con monto y vencimiento',
      coins: 'Monedas',
      coinsRenewed: 'Monedas renovadas correctamente',
      coinsWithAmount: 'Tus monedas virtuales con monto y vencimiento',
      completeBoarding: 'Completar boarding',
      completeBoardingAndRenew: 'Completar boarding y renovar',
      date: 'Fecha',
      expiringSoon: 'expira pronto',
      failedToFetchCoins: 'Error al obtener monedas',
      firstExpiration: 'Primer vencimiento de moneda virtual: {time}.',
      nextMarketHour: 'Próxima hora de mercado: {date} ({ago}) por {duration}.',
      nextRenewal: 'Próxima renovación',
      renewVirtualCoins: 'Renovar monedas virtuales',
      renewing: 'Renovando',
      settled: 'liquidado',
      settlementFees: 'La liquidación durante horas de mercado ofrece comisiones más bajas.',
      subdust: 'subdust',
      swept: 'recuperable',
      title: 'Monedas virtuales',
      unconfirmed: 'sin confirmar',
      virtualCoinRenewal: 'Renovación de moneda virtual',
    },
    wallet: {
      accounts: 'Cuentas',
      activity: 'Actividad',
      addArkadeToHome: 'Añadir Arkade a tu pantalla de inicio',
      install: 'Instalar',
      installAndroidDescription: "Toca 'Instalar' para añadir Arkade a tu pantalla de inicio.",
      installIosDescription: 'Toca el icono de compartir en Safari y luego "Añadir a pantalla de inicio".',
      openSettings: 'Abrir ajustes',
      receive: 'Recibir',
      recentActivity: 'Actividad reciente',
      scan: 'Escanear',
      send: 'Enviar',
      swap: 'Intercambiar',
      syncing: 'Sincronizando...',
      tapToGoHome: 'Toca para ir a la pantalla de inicio.',
      transaction: 'Transacción',
      viewAll: 'Ver todo',
      viewAllActivity: 'Ver toda la actividad',
      viewRecentActivity: 'Ver actividad reciente',
      walletActions: 'Acciones de la wallet',
    },
  },
  pt: {
    accounts: {
      accountUnavailable: 'Conta indisponível',
      address: 'Endereço',
      arknote: 'Arknote',
      assetAmount: 'Valor do ativo',
      assetAmountTicker: 'Valor do ativo ({ticker})',
      assetAmountTickerUnverified: 'Valor do ativo ({ticker}, não verificado)',
      assetId: 'ID do ativo',
      completed: 'Concluído',
      corridor: 'Corredor',
      date: 'Data',
      destination: 'Destino',
      direction: 'Direção',
      expiry: 'Expiração',
      funded: 'Financiado',
      invoice: 'Fatura',
      l1HtlcAddress: 'Endereço L1 HTLC',
      loadingPriceHistory: 'Carregando histórico de preços…',
      networkFees: 'Taxas de rede',
      paidOnchain: 'Pago on-chain',
      priceChartRange: 'Período do gráfico de preços',
      priceHistoryUnavailable: 'Histórico de preços indisponível',
      priceRate: 'Taxa de preço',
      priceUnavailable: 'Preço indisponível',
      rail: 'Via',
      receivedAt: 'Recebido em',
      recipientGets: 'O destinatário recebe',
      refundDeadline: 'Prazo de reembolso',
      solver: 'Solver',
      status: 'Estado',
      swapFees: 'Taxas de swap',
      swapFrom: 'Trocar de',
      swapTo: 'Trocar para',
      total: 'Total',
      transactionId: 'ID da transação',
      type: 'Tipo',
      unverifiedAssetAmount: 'Valor do ativo não verificado',
      valueUnavailable: 'Indisponível',
      when: 'Quando',
    },
    appIntent: {
      address: 'Endereço',
      addressFailed: 'Não foi possível ler seu endereço.',
      badCallback: 'O link de retorno deve ser um endereço https.',
      badRequest: 'Este link de pagamento não contém uma solicitação Bitcoin válida.',
      deny: 'Não compartilhar',
      errorTitle: 'Solicitação do app',
      goHome: 'Ir para a carteira',
      missingCallback: 'Este link não tem um destino de retorno.',
      missingRequest: 'Falta a solicitação neste link de pagamento.',
      open: 'Abrir',
      openInApp: 'Abrir no app',
      openInAppHint: 'Conclua esta solicitação no app Arkade instalado.',
      openInAppSafari:
        'O Safari não pode abrir o app instalado a partir desta página. Abra o Arkade pela tela inicial ou conclua a solicitação aqui.',
      pubkey: 'Chave pública',
      returnTo: 'Voltar para {host}',
      sentReturn: '{amount} enviado. Toque para voltar para {host}',
      share: 'Compartilhar endereço',
      shareExplainer:
        'Isso compartilha seu endereço de recebimento e sua chave pública. Não permite gastar seus fundos.',
      title: 'Conectar',
      tooLong: 'Este link é longo demais.',
      unknownAction: 'Este link não é uma solicitação conhecida.',
      wantsAddress: '{host} solicita seu endereço Arkade.',
    },
    apps: {
      dfx: 'DFX',
      dfxConnectionFailed: 'Não foi possível conectar à DFX. Volte e tente novamente.',
    },
    backup: {
      backupFailed: 'Falha no backup do Nostr',
      cantRecover: 'Se você perder, não poderá recuperá-lo',
      confirm: 'Confirmar',
      copyToClipboard: 'Copiar para a área de transferência',
      doNotShare: 'Não compartilhe com ninguém',
      enableNostrBackups: 'Ativar backups do Nostr',
      enoughToRestore: 'Isso é suficiente para restaurar sua carteira.',
      forYourEyesOnly: 'Só para você; não compartilhe.',
      keepSafe: 'Mantenha sua {secret} em segurança',
      keyBacksUpWallet:
        'Sua chave privada é usada para fazer backup da sua carteira. Mantenha-a sempre em segredo e em segurança.',
      nostrBackupUpdated: 'Backup do Nostr atualizado',
      nostrBackupsSubtext: 'Ativar ou desativar os backups do Nostr',
      phraseBacksUpWallet:
        'Sua frase de recuperação é usada para fazer backup da sua carteira. Mantenha-a sempre em segredo e em segurança.',
      phraseWarning:
        'Sua frase de recuperação pode ser usada para acessar tudo na sua carteira. Não a compartilhe com ninguém.',
      privateKey: 'Chave privada',
      privateKeyWarning:
        'Sua chave privada pode ser usada para acessar tudo na sua carteira. Não a compartilhe com ninguém.',
      recoveryPhrase: 'Frase de recuperação',
      viewPrivateKey: 'Ver chave privada',
      viewRecoveryPhrase: 'Ver frase de recuperação',
    },
    browser: {
      copied: 'Copiado',
      copyLink: 'Copiar link',
      notSupported: 'Este navegador não é compatível.',
      stepBrowser: 'Abra o Safari, o Chrome ou seu navegador',
      stepCopy: 'Copie o link abaixo',
      stepPaste: 'Cole o link e acesse',
      welcome: 'Boas-vindas à Arkade 👾',
    },
    common: {
      address: 'Endereço',
      all: 'Tudo',
      amount: 'Valor',
      back: 'Voltar',
      balance: 'Saldo',
      cancel: 'Cancelar',
      close: 'Fechar',
      continue: 'Continuar',
      copied: 'Copiado!',
      copiedToClipboard: 'Copiado para a área de transferência',
      dismiss: 'Dispensar',
      failedToCopy: 'Não foi possível copiar',
      generateOrImportCoins: 'Gere ou importe moedas para continuar',
      gotIt: 'Entendi',
      home: 'Início',
      importOrMintToStart: 'Importe ou emita um ativo para começar',
      learnMore: 'Saiba mais',
      loading: 'Carregando...',
      makeTransactionToStart: 'Faça uma transação para começar.',
      network: 'Rede',
      noAssetsYet: 'Ainda não há ativos',
      noLogsAvailable: 'Não há registros disponíveis',
      noTransactionsYet: 'Ainda não há transações',
      noVirtualCoins: 'Não há moedas virtuais disponíveis',
      off: 'Desativado',
      on: 'Ativado',
      paste: 'Colar',
      sats: 'sats',
      save: 'Salvar',
      shortly: 'em breve',
      startUsingToGenerateLogs: 'Comece a usar o app para gerar registros.',
      success: 'Sucesso',
      total: 'Total',
      tryAgain: 'Tentar novamente',
      unknown: 'Desconhecido',
      value: 'Valor',
    },
    components: {
      arkadeAddress: 'Endereço Arkade',
      btcAddress: 'Endereço BTC',
      cameraBlocked:
        'O acesso à câmera está bloqueado. Permita o acesso a este site nas configurações do navegador e tente novamente.',
      cameraNotAvailable: 'Câmera indisponível',
      clear: 'Limpar',
      clearAmount: 'Limpar valor',
      confirmPassword: 'Confirmar senha',
      copyAddress: 'Copiar endereço',
      filterActivity: 'Filtrar atividade',
      focusableElement: 'Elemento focável',
      hideBalances: 'Ocultar saldos',
      lightningInvoice: 'Fatura Lightning',
      lnurlAddress: 'LNURL',
      noPasswordYolo: 'Sem senha, por sua conta e risco',
      password: 'Senha',
      passwordMinChars: 'pelo menos 8 caracteres',
      passwordOneNumber: 'conter pelo menos 1 número',
      passwordSpecialChar: 'conter pelo menos 1 caractere especial',
      passwordsMustMatch: 'As senhas devem ser iguais',
      protectWalletWithPassword: 'Proteja sua carteira com uma senha',
      recoveryPhraseOrKey: 'Frase de recuperação ou chave privada',
      reload: 'Recarregar',
      reloadToContinue: 'Recarregue para continuar',
      save: 'Salvar',
      savePassword: 'Salvar senha',
      scanQr: 'Escanear QR',
      setMaximumAmount: 'Definir valor máximo',
      showBalances: 'Mostrar saldos',
      somethingWentWrong: 'Algo deu errado',
      strongPasswordWith: 'Crie uma senha forte com:',
      toggleCurrency: 'Trocar moeda',
      toggleVisibility: 'Alternar visibilidade',
      unexpectedError: 'O app encontrou um erro inesperado',
    },
    contracts: {
      active: 'Ativo',
      deprecatedSigner: 'assinante obsoleto',
      deprecatedSignerCutoff: 'assinante obsoleto · prazo expirado',
      inactive: 'Inativo',
      noContracts: 'Nenhum contrato encontrado.',
      parameters: 'Parâmetros',
      pastRefundLocktime: 'Tempo de reembolso expirado',
      refundLocktime: 'Tempo de reembolso',
      script: 'Script',
      searchPlaceholder: 'Buscar tipo, endereço, script…',
    },
    delegate: {
      active: 'Ativo',
      addressLabel: 'endereço: {value}',
      delegateDescription:
        'Um delegado é um terceiro de confiança que você escolhe para ajudar a manter seus VTXOs seguros.',
      delegatesRenewOnly:
        'Os delegados só podem renovar seus VTXOs; não podem gastar seus fundos nem controlar sua carteira.',
      feeLabel: 'fee: {value}',
      inactive: 'Inativo',
      nextRenewal: 'próxima renovação {time}',
      noDelegateFound: 'Nenhum delegado encontrado para esta rede.',
      noUpcomingRenewal: 'Nenhuma renovação próxima',
      pubkeyLabel: 'pubkey: {value}',
      useDefaultArkadeDelegate: 'Usar o delegado padrão da Arkade',
      useDefaultSubtext: 'Use o delegado padrão da Arkade para gerenciar as renovações',
      walletReloadToApply: 'A carteira será recarregada para aplicar a alteração.',
      whatIsADelegate: 'O que é um delegado?',
    },
    errors: {
      amountBetween: 'O valor deve estar entre {min} e {max}',
      outdatedWallet:
        'Sua carteira está desatualizada e precisa ser atualizada para ser compatível com a versão mais recente da Arkade.',
      recentlySettledHours: 'Seus fundos foram liquidados on-chain recentemente — tente novamente em algumas horas',
      recentlySettledRetry: 'Seus fundos foram liquidados on-chain recentemente — tente novamente {when}',
    },
    formatting: {
      ago: 'há {value}',
      ahead: 'em {value}',
      day: 'dia',
      days: 'dias',
      hour: 'hora',
      hours: 'horas',
      justNow: 'agora mesmo',
      minute: 'minuto',
      minutes: 'minutos',
      second: 'segundo',
      seconds: 'segundos',
      unit: '{count} {unit}',
    },
    init: {
      addressRotation: 'Alternância de endereços',
      arkadeServerUnreachable: 'Servidor Arkade inacessível',
      biometricsDescription:
        'Isso permitirá que você entre facilmente usando dados biométricos, sem precisar lembrar a senha.',
      connectingToServer: 'Conectando ao servidor',
      connectingToServerStatus: 'Conectando ao servidor',
      connectionFailed: 'Falha na conexão',
      createNewWallet: 'Criar nova carteira',
      createPasskey: 'Criar chave de acesso',
      createReadyText: 'Sua carteira foi criada e já está pronta para uso.',
      createWallet: 'Criar carteira',
      goToWallet: 'Ir para a carteira',
      newWalletLive: 'Sua nova carteira está pronta!',
      otherLoginOptions: 'Outras opções de acesso',
      recoveryChecksumError: 'A frase de recuperação é inválida. Verifique as palavras e a ordem.',
      recoveryInputHint: 'Digite ou cole sua frase de recuperação ou chave privada.',
      recoveryInstructions:
        'Insira sua frase de recuperação de 12 palavras ou uma chave privada que comece com "nsec", ou uma chave hexadecimal. Não compartilhe com ninguém.',
      recoveryKeyError: 'Insira uma chave privada nsec válida ou uma chave privada hexadecimal de 64 caracteres.',
      recoveryOneWord: '1 palavra inserida',
      recoveryWordCount: '{count} palavras inseridas',
      recoveryWordCountError: 'Insira a frase de recuperação completa: 12, 15, 18, 21 ou 24 palavras.',
      recoveryWordError: 'A palavra {number} não foi reconhecida. Verifique a ortografia.',
      restoreReadyText: 'Sua carteira foi restaurada e já está pronta para uso.',
      restoreSuccessText: 'Sua carteira, configurações e swaps foram restaurados.',
      restoreWallet: 'Restaurar carteira',
      restoringWallet: 'Restaurando carteira...',
      rotateReceiveAddresses: 'Alternar endereços de recebimento',
      rotateReceiveSubtext:
        'Deriva um novo endereço para cada pagamento recebido (carteira HD). Isso melhora a privacidade on-chain. Recomenda-se ativar os backups do Nostr para que os endereços alternados possam ser recuperados ao restaurar. Para usuários avançados.',
      rotationSubtext:
        'Herdar usa as configurações salvas da carteira (geralmente restauradas do backup). Se o backup não estiver disponível, serão usadas as configurações locais/padrão. Escolha HD se esta carteira alternou endereços de recebimento e você precisar forçar a recuperação HD.',
      tagline1: 'Pagamentos rápidos, swaps e muito mais',
      tagline2: 'Acesse a Lightning, emita ativos e muito mais. Tudo protegido pelo Bitcoin',
      tagline3: 'Você tem o controle. Liquide e saque quando quiser',
      useBiometrics: 'Usar dados biométricos',
      usePassword: 'Usar senha',
      walletRestored: 'Carteira restaurada com sucesso!',
      wordSuggestions: 'Sugestões de palavras',
    },
    loading: {
      burning: 'Queimando...',
      connectingToDfx: 'Conectando à DFX...',
      connectingToServiceWorker: 'Conectando ao service worker...',
      fetchAssetDetails: 'Buscando detalhes do ativo...',
      fetchingCoins: 'Buscando moedas...',
      fetchingTransactions: 'Buscando transações...',
      loadingAsset: 'Carregando ativo...',
      loadingAssetMetadata: 'Carregando metadados dos ativos...',
      loadingAssets: 'Carregando ativos...',
      loadingWalletData: 'Carregando dados da carteira...',
      mintingAsset: 'Emitindo ativo...',
      mintingControlAsset: 'Emitindo ativo de controle...',
      payingToMainnet: 'Pagando na mainnet. Isso pode levar alguns instantes.',
      recoveringAddresses: 'Recuperando endereços...',
      reissuing: 'Reemitindo...',
      renewing: 'Renovando suas moedas virtuais. Isso pode levar alguns instantes.',
      retryingConnection: 'Tentando conectar novamente...',
      settlingTransactions: 'Liquidando transações. Isso pode levar alguns instantes.',
      startingWallet: 'Iniciando a carteira...',
      updatingBalance: 'Atualizando saldo...',
    },
    logs: {
      logAt: 'Registro de {time} com a mensagem {msg}. Pressione Escape para sair da navegação pelo teclado.',
      pressEnter: 'Pressione Enter para ativar a navegação pelo teclado nos registros',
    },
    mint: {
      additionalAmount: 'Valor adicional',
      amount: 'Valor *',
      amountMustBePositive: 'O valor deve ser um número positivo',
      amountToBurn: 'Valor a queimar',
      arkadeMint: 'Arkade Mint',
      arkadeMintDisabled: 'Arkade Mint está desativado.',
      assetCreated: 'Ativo criado',
      assetId: 'ID do ativo',
      assetIdMustBeHex: 'O ID do ativo deve ser uma sequência hexadecimal de 68 caracteres',
      assetIdRequired: 'O ID do ativo é obrigatório',
      assetIdTapToCopy: 'ID do ativo (toque para copiar)',
      assetMinted: 'Ativo emitido!',
      assetNotFound: 'Ativo não encontrado',
      backToArkadeMint: 'Voltar ao Arkade Mint',
      burn: 'Queimar',
      burnTitle: 'Queimar {ticker}',
      burnWarning: 'Você está prestes a queimar {amount} {ticker}. Esta ação é irreversível.',
      confirmBurn: 'Confirmar queima',
      confirmReissue: 'Confirmar reemissão',
      controlAmount: 'Valor de controle',
      controlAmountPositive: 'O valor de controle deve ser positivo',
      controlAsset: 'Ativo de controle',
      decimals: 'Casas decimais',
      decimalsRange: 'As casas decimais devem estar entre 0 e {max}',
      enableArkadeMint: 'Ativar Arkade Mint',
      enableArkadeMintSubtext: 'Ativar ou desativar o Arkade Mint',
      enableIt: 'Ative-o',
      enterAmount: 'Insira um valor',
      enterControlAmount: 'Insira o valor do ativo de controle',
      enterName: 'Insira um nome',
      enterTicker: 'Insira um ticker',
      exceedsBalance: 'Você não pode queimar mais do que seu saldo ({balance} {ticker})',
      existing: 'Existente',
      hideIcon: 'Ocultar ícone',
      iconUrl: 'URL do ícone',
      import: 'Importar',
      importAsset: 'Importar ativo',
      invalidDecimals: 'As casas decimais devem ser um número inteiro entre 0 e {max}',
      invalidIconUrl: 'URL do ícone inválida',
      max: 'Máx.',
      mint: 'Emitir',
      myToken: 'Meu token',
      name: 'Nome *',
      nameLabel: 'Nome',
      nameTooLong: 'O nome deve ter no máximo 40 caracteres',
      new: 'Novo',
      none: 'Nenhum',
      pasteAssetId: 'Colar ID do ativo...',
      receive: 'Receber',
      reissue: 'Reemitir',
      reissueTitle: 'Reemitir {ticker}',
      reissueWarning: 'Você está prestes a emitir mais {amount} de {ticker}.',
      remove: 'Remover',
      selectFromWallet: 'Selecionar da carteira...',
      send: 'Enviar',
      settingsTitle: 'Configurações do Arkade Mint',
      showIcon: 'Mostrar ícone',
      supply: 'Oferta',
      ticker: 'Ticker *',
      tickerLabel: 'Ticker',
      tickerTooLong: 'O ticker deve ter no máximo 8 caracteres',
      title: 'Emitir ativo',
      toViewYourAssets: 'para ver seus ativos.',
      unknown: 'Desconhecido',
      unknownAsset: 'Ativo desconhecido',
      viewAsset: 'Ver ativo',
    },
    notes: {
      arkNote: 'Nota Ark',
      noteRedeemed: 'Nota resgatada!',
      processing: 'Processando. Isso pode levar alguns instantes.',
      redeemNote: 'Resgatar nota',
      redeemedSuccessfully: 'resgatada com sucesso',
      redeeming: 'Resgatando...',
      soundsGood: 'Ótimo!',
      title: 'Nota',
    },
    notifications: {
      apiNotSupported: 'A API de notificações não é compatível',
      permissionDenied: 'Permissão para notificações negada',
    },
    receive: {
      addAmount: 'Adicionar valor',
      addName: 'Adicionar nome',
      amount: 'Valor',
      arkadeAddress: 'Endereço Arkade',
      backToMethod: 'Voltar ao método',
      bitcoinAddress: 'Endereço Bitcoin',
      chooseName: 'Escolher nome',
      chooseNameOptional: 'Escolher nome (opcional)',
      claimCode: 'Código de resgate',
      claimCodeInvalid: 'Código de resgate inválido',
      claimCodeNeedsName: 'O código de resgate requer um nome',
      claimCodeOptional: 'Código de resgate (opcional)',
      claimFailed: 'Falha ao resgatar o pagamento Lightning: {error}',
      claimModeUnavailable: 'Modo de resgate indisponível',
      claimName: 'Nome de resgate',
      claimNotAllowed: 'Resgate não permitido',
      claimRateLimited: 'Demasiadas tentativas, tente novamente mais tarde',
      claimTaken: 'Esse nome já está em uso',
      clearAmount: 'Limpar valor',
      copy: 'Copiar',
      copyAddress: 'Copiar endereço',
      copyAria: 'Copiar {title}',
      copyQrCode: 'Copiar código QR',
      editAmount: 'Editar valor',
      failedToGetAddress: 'Erro ao obter o endereço: {error}',
      generatingInvoice: 'Gerando fatura…',
      getNewQuote: 'Obter nova cotação',
      gettingQuote: 'Obtendo cotação…',
      haveClaimCode: 'Já possui um código de resgate?',
      invalidNote: 'Nota inválida',
      lightningInvoice: 'Fatura Lightning',
      lightningPaymentLost: 'Pagamento Lightning perdido: o solver o resgatou antes que pudesse ser recebido',
      lightningUnavailable: 'Lightning indisponível: {error}',
      lnaddressClaimFailed: 'Falha ao resgatar LNAddress',
      lnaddressUnavailable: 'LNAddress indisponível',
      methodArkade: 'Arkade',
      methodBitcoin: 'Bitcoin',
      methodLightning: 'Lightning',
      noInvoiceForAmount: 'Nenhuma fatura para este valor',
      noLightningSolver: 'Nenhum solver Lightning disponível',
      noPaymentMethods: 'Não há métodos de pagamento válidos para este valor',
      noTokenQuote: 'Nenhuma cotação de token',
      openInWallet: 'Abrir na carteira',
      payWithStablecoins: 'Pagar com stablecoins',
      pickForMe: 'Escolher para mim',
      quoteExpired: 'Cotação expirada',
      quoteExpiredSub: 'A cotação que você estava visualizando expirou. Obtenha uma nova cotação.',
      requestingAmount: 'Solicitando {amount} {unit}',
      setAmount: 'Definir valor',
      share: 'Compartilhar',
      tokenAmount: 'Quantidade de token',
      tokenDepositAddress: 'Endereço de depósito do token',
      tokenQuoteValid: 'Cotação de token válida',
      tokenSendExactly: 'Enviar exatamente',
      unified: 'Unificado',
    },
    scanner: {
      cameraError: 'Câmera indisponível',
      tryAgain: 'Tentar novamente',
    },
    send: {
      amountAboveLnurlMax: 'Valor acima do máximo LNURL',
      amountAboveMax: 'Valor acima do máximo',
      amountBelowLnurlMin: 'Valor abaixo do mínimo LNURL',
      amountBelowMin: 'Valor abaixo do mínimo',
      amountBelowOneSat: 'Valor abaixo de 1 satoshi',
      amountTooLow: 'Valor muito baixo',
      amountToSend: 'Valor a enviar',
      arkadeServerKeyMismatch: 'A chave do servidor Arkade não corresponde',
      asset: 'Ativo',
      assetsOnlyToArkade: 'Ativos só podem ser enviados para endereços Arkade',
      available: '{amount} disponível',
      balanceReserve: 'Reserva de saldo',
      balanceReserveText:
        '{dust} sats são mantidos em reserva para proteger seus ativos. Seu valor máximo de envio é {max} sats.',
      bitcoin: 'Bitcoin',
      continue: 'Continuar',
      directionSendingAssets: 'Enviando ativos',
      errorSendingTransaction: 'Erro ao enviar a transação',
      feeChanged: 'A taxa de rede mudou. Revise o novo total e toque para assinar novamente',
      feesDeductedFromAmount: 'As taxas serão descontadas do valor enviado',
      gettingQuote: 'Obtendo cotação',
      insufficientAssetBalance: 'Saldo de ativos insuficiente',
      insufficientFunds: 'Saldo insuficiente',
      insufficientFundsDetail: 'Saldo insuficiente; você tem apenas {balance} sats',
      insufficientFundsForLnurl: 'Saldo insuficiente para LNURL',
      insufficientFundsMax: 'Saldo insuficiente. Após as taxas de rede, você pode enviar até {max} sats',
      invalidAmount: 'Valor inválido',
      invalidArkadeAddressFromLnurl: 'Endereço Arkade inválido recebido do LNURL',
      invalidRecipient: 'Endereço do destinatário inválido',
      invoiceMustHaveAmount: 'A fatura precisa ter um valor definido',
      lnurlNotFound: 'LNURL não encontrado',
      missingAddress: 'Endereço ausente',
      missingAmount: 'Valor ausente',
      missingAssetAmount: 'Valor do ativo ausente',
      missingSatoshisAmount: 'Valor em satoshis ausente',
      missingTotalAmount: 'Valor total ausente',
      onTheWay: '{amount} a caminho',
      onTheWayKeepOpen: '{amount} a caminho — mantenha a carteira aberta até que seja concluído',
      payingInsideArkade: 'Pagando dentro da Arkade',
      payingToLightning: 'Pagando via Lightning',
      payingToMainnet: 'Pagando na mainnet',
      paymentOnTheWay: 'O pagamento está a caminho',
      paymentSent: 'Pagamento enviado',
      pendingOnchain: 'Um pagamento on-chain ainda está a caminho — mantenha a carteira aberta até que seja concluído',
      quoteForDifferentInvoice: 'Cotação para fatura diferente',
      quoteForDifferentPayment: 'Cotação para pagamento diferente',
      recipientAddress: 'Endereço do destinatário',
      sendMax: 'Enviar máximo',
      sendingOffchainNotAllowed: 'O envio off-chain não é permitido',
      sendingOnchainNotAllowed: 'O envio on-chain não é permitido',
      sentSuccessfully: '{amount} enviado com sucesso',
      serverUnreachable: 'Servidor inacessível',
      signTransaction: 'Assinar transação',
      tapToSign: 'Toque para assinar',
      title: 'Enviar',
      unableToDecodeInvoice: 'Não foi possível decodificar a fatura',
      unableToGetReceivingAddresses: 'Não foi possível obter os endereços de recebimento',
      unableToParseBip21: 'Não foi possível analisar o BIP21',
      unknownAsset: 'Ativo desconhecido',
      verifiedByBranta: 'Verificado pela Branta',
      verifyingAddress: 'Verificando endereço...',
    },
    settings: {
      about: 'Sobre',
      advanced: 'Avançado',
      allowNotifications: 'Permitir notificações',
      appearance: 'Aparência',
      arkadeMint: 'Arkade Mint',
      auto: 'Automático',
      backup: 'Backup',
      bitcoinUnit: 'Unidade do bitcoin',
      boardingExitDelay: 'Atraso para saída de boarding',
      bugReports: 'Relatos de erros',
      bugReportsText:
        'Relate qualquer problema ou erro encontrado. Inclua as etapas para reproduzi-lo e agilizar a resolução.',
      cannotUndo: 'Esta operação não pode ser desfeita.',
      changePassword: 'Alterar senha',
      clear: 'Limpar',
      connectToServer: 'Conectar ao servidor',
      contracts: 'Contratos',
      currency: 'Moeda',
      customerSupport: 'Atendimento ao cliente',
      customerSupportText:
        'Receba ajuda com sua carteira, relate erros ou tire dúvidas. Nossa equipe de suporte está aqui para ajudar.',
      dark: 'Escuro',
      delegates: 'Delegados',
      didYouBackup: 'Você fez backup da sua carteira?',
      display: 'Tela',
      dust: 'Dust',
      english: 'Inglês',
      exportToCsv: 'Exportar para arquivo CSV',
      failedToLoadSupportChat: 'Erro ao carregar o chat de suporte',
      failedToUpdatePassword: 'Erro ao atualizar a senha',
      feedback: 'Feedback',
      forfeitAddress: 'Endereço de forfeiture',
      general: 'Geral',
      gitCommitHash: 'Hash do commit git',
      hapticFeedback: 'Feedback tátil',
      hapticSubtext: 'Vibração ao tocar nos botões e interagir',
      iHaveBackedUp: 'Fiz backup da minha carteira',
      language: 'Idioma',
      light: 'Claro',
      lightningAddress: 'Endereço Lightning',
      lnurlAddName: 'Adicionar nome LNURL',
      lnurlLimits: 'Limites LNURL',
      lnurlNone: 'Nenhum LNURL',
      lnurlPrimary: 'LNURL principal',
      lnurlServer: 'Servidor LNURL',
      lnurlYours: 'O seu endereço Lightning',
      lock: 'Bloqueio',
      lockExplanation: 'Depois de bloquear, será necessário digitar sua senha para desbloquear.',
      lockWallet: 'Bloquear carteira',
      lockYourWallet: 'Bloquear sua carteira',
      logs: 'Registros',
      noPasswordDefined: 'Nenhuma senha definida',
      notes: 'Notas',
      notifications: 'Notificações',
      notificationsIntro:
        'Receba uma notificação quando houver uma atualização ou um pagamento. Sua permissão será solicitada, se necessário.',
      notificationsUnsupported:
        'Seu navegador não é compatível com a API de Notificações. No iOS, é necessário "Adicionar à Tela de Início" e usar o iOS 16.4 ou superior.',
      openSupportChat: 'Abrir chat de suporte',
      passwordChanged: 'Senha alterada',
      passwordChangedToBiometrics: 'Senha substituída pelos dados biométricos',
      passwordRemoved: 'Senha removida',
      portuguese: 'Português',
      preferences: 'Preferências',
      receive: 'Receber',
      receiveViaTokens: 'Receber via tokens',
      receiveViaTokensSubtext: 'Receba pagamentos usando tokens específicos.',
      resetFailed: 'Falha ao redefinir',
      resetWallet: 'Redefinir carteira',
      sameServer: 'Mesmo servidor',
      secureChat: 'Chat seguro',
      secureChatText: 'Suas conversas são seguras e privadas. O histórico é mantido entre as sessões.',
      security: 'Segurança',
      server: 'Servidor',
      serverFound: 'Servidor encontrado',
      serverPubkey: 'Chave pública do servidor',
      serverResetWarning: 'Sua carteira será redefinida. Faça backup antes de continuar.',
      serverUnavailableOnMainnet: 'As configurações do servidor não estão disponíveis na mainnet.',
      serverUrl: 'URL do servidor',
      sessionDuration: 'Duração da sessão',
      setPassword: 'Definir senha',
      setPasswordToLock: 'Você precisa definir uma senha para bloquear.',
      solvers: 'Solvers',
      spanish: 'Espanhol',
      success: 'Sucesso',
      support: 'Suporte',
      supportChatNotConfigured: 'O chat de suporte não está configurado',
      theme: 'Tema',
      title: 'Configurações',
      tokenExplainer:
        'Quem paga envia tokens para {provider}, um terceiro que mantém o depósito enquanto paga uma fatura Lightning. Em seguida, um swap de intent-solver entrega esse pagamento a você como BTC da Arkade. Um depósito atrasado, insuficiente ou excedente é resolvido com {provider}.',
      tokenExplainerConfirm: 'Activar',
      tokenExplainerTitle: 'Antes de aceitar tokens',
      trackProgress: 'Acompanhe o andamento',
      trackProgressText:
        'Todos os seus chamados e conversas são salvos. Você pode ver conversas anteriores a qualquer momento.',
      unableToConnect: 'Não foi possível conectar',
      unilateralExitDelay: 'Atraso para saída unilateral',
      useBiometrics: 'Usar dados biométricos',
      vtxos: 'Moedas virtuais',
      walletMode: 'Modo da carteira',
    },
    solvers: {
      addNew: '+ Adicionar novo',
      buildShipsPlural: 'Esta versão inclui {count} cartões de solver; adicione os seus para acessar mais solvers.',
      buildShipsSingular: 'Esta versão inclui {count} cartão de solver; adicione os seus para acessar mais solvers.',
      builtIn: 'Integrado',
      builtInPlusOwnPlural: '{builtIn} cartões de solver integrados e mais {own} próprios.',
      builtInPlusOwnSingular: '{builtIn} cartão de solver integrado e mais {own} próprio.',
      cardInfo: 'Informações do cartão',
      confirmRemove: 'Confirmar remoção',
      currentLabel: 'Atual',
      edit: 'Editar',
      failedRemove: 'Erro ao remover o cartão: o armazenamento está cheio ou indisponível.',
      failedSave: 'Erro ao salvar o cartão: o armazenamento está cheio ou indisponível.',
      fieldBase: 'Base',
      fieldDiscoveryKey: 'Chave de descoberta',
      fieldFee: 'Taxa',
      fieldName: 'Nome',
      fieldQuote: 'Cotação',
      fieldVersion: 'Versão',
      hideRaw: 'Ocultar JSON',
      invalidCard: 'cartão inválido: {message}',
      invalidJson: 'JSON inválido: {message}',
      marketsLabel: 'Mercados',
      noStoredCards: 'Você não tem cartões de solver salvos na sua carteira.',
      originalLabel: 'Original',
      publishedMarkets: 'Mercados publicados pelo solver',
      remoteError: 'Não foi possível carregar o cartão atual.',
      remoteChecking: 'Verificando o cartão atual…',
      remoteNoRelays: 'Apenas local — nenhum relay Nostr neste cartão.',
      remoteNotFound: 'O cartão atual não foi encontrado em seus relays.',
      remove: 'Remover',
      removeWarning: 'Tem certeza de que deseja remover o cartão "{label}"? Esta ação não pode ser desfeita.',
      storedPlural: 'Você tem {count} cartões de solver salvos na sua carteira.',
      storedSingular: 'Você tem {count} cartão de solver salvo na sua carteira.',
      viewInfo: 'Ver informações',
      viewRaw: 'Ver raw JSON',
    },
    swap: {
      amountTooSmall: 'Valor muito baixo',
      asset: 'ativo',
      assetAmount: 'valor do ativo',
      chooseAsset: 'Escolher ativo',
      chooseAssetToReceive: 'Escolha o ativo para receber',
      chooseAssetToSwap: 'Escolha o ativo para trocar',
      comingSoon: 'Swaps em breve',
      comingSoonText: 'Estamos aprimorando a experiência de swap antes de disponibilizá-la aqui.',
      confirmSwap: 'Confirmar swap',
      createdTapHome: 'Swap criado. Toque para ir à tela inicial.',
      currencyAmount: 'valor em {currency}',
      deleteDigit: 'Apagar dígito',
      fees: 'Taxas',
      insufficientBalance: 'Saldo insuficiente',
      keypadFor: 'Teclado de swap para {amount}',
      max: 'Máx.',
      maximum: 'Máximo',
      minimum: 'Mínimo',
      noAssetsMatch: 'Nenhum ativo corresponde a esta busca',
      pairUnavailable: 'Swap indisponível para este par',
      partialSwapNeedsBtc:
        'Você não tem bitcoin suficiente para fazer uma troca parcial. Troque o valor total ou obtenha bitcoin.',
      pending: 'Pendente',
      pickAssetForSide: 'Escolha o ativo para este lado do swap.',
      quoteUnavailable: 'Cotação indisponível',
      receive: 'Receber',
      reviewSwap: 'Revisar swap',
      reviewSwapDescription: 'Confira a rota e os totais estimados.',
      routeConnector: 'para',
      searchAssets: 'Buscar ativos',
      selectAssetToTradeFrom: 'Selecione o ativo que deseja usar na troca.',
      showFirst: 'Mostrar {mode} primeiro',
      swapCreated: 'Swap criado',
      swapRoute: 'Rota do swap',
      swapsUnavailable: 'Os swaps não estão disponíveis',
      swapsUnavailableText: 'Adicione outro ativo compatível para trocar entre saldos.',
      switchSwapDirection: 'Inverter a direção do swap',
      title: 'Troca',
      useMaximum: 'Usar o máximo {balance}',
      waitingForFill: 'Aguardando execução',
    },
    table: {
      keyboardNavIntro: 'Pressione Enter para ativar a navegação pelo teclado na tabela',
      keyboardNavTitle: 'Título {title} com estado {value}. Pressione Escape para sair da navegação pelo teclado.',
    },
    transaction: {
      addReminder: 'Adicionar lembrete',
      amountBurned: 'Valor queimado',
      amountExited: 'Valor sacado',
      amountIssued: 'Valor emitido',
      amountReceived: 'Valor recebido',
      amountReissued: 'Valor reemitido',
      amountSent: 'Valor enviado',
      assetId: 'ID do ativo',
      assetIdTicker: 'ID do ativo ({ticker})',
      assetIdTickerUnverified: 'ID do ativo ({ticker}, não verificado)',
      assetIdUnverified: 'ID do ativo (não verificado)',
      boardingExpired: 'A transação de boarding expirou.',
      burn: 'Queima',
      cancelSwap: 'Cancelar swap',
      cancelSwapBody: 'Se o swap ainda estiver pendente, isso devolverá à sua carteira os fundos bloqueados.',
      cancelSwapTitle: 'Cancelar o swap?',
      cancelled: 'Cancelada',
      cancelling: 'Cancelando…',
      completeBoarding: 'Concluir boarding',
      completed: 'Concluída',
      exited: 'Saída',
      expired: 'Expirada',
      failed: 'Falhada',
      fromAssetId: 'ID do ativo de origem',
      fromAssetIdUnverified: 'ID do ativo de origem (não verificado)',
      issuance: 'Emissão',
      keepSwap: 'Manter swap',
      keyboardNavAria: 'Transação {type} no valor de {amount}. Pressione Escape para sair da navegação pelo teclado.',
      keyboardNavHint: 'Pressione Enter para ativar a navegação pelo teclado na lista de transações',
      lightningReceive: 'Recebimento Lightning',
      lightningReceiveFailed: 'Falha no recebimento Lightning',
      lightningReceiveLost: 'Recebimento Lightning perdido',
      lightningReceivePending: 'Recebimento Lightning pendente',
      lightningReceiveRefunded: 'Recebimento Lightning reembolsado',
      lightningSend: 'Envio Lightning',
      lightningSendFailed: 'Falha no envio Lightning',
      lightningSendLost: 'Envio Lightning perdido',
      lightningSendPending: 'Envio Lightning pendente',
      lightningSendRefunded: 'Envio Lightning reembolsado',
      lnurlReceivedAt: 'LNURL recebido em',
      lnurlReceivedVia: 'LNURL recebido via',
      lnurlSentTo: 'LNURL enviado para',
      lnurlSentVia: 'Enviado via {rail}',
      paymentReceived: 'Pagamento recebido',
      paymentReceivedAria: 'Pagamento recebido com sucesso. Toque para ir à tela inicial.',
      pending: 'Pendente',
      pendingBoarding: 'Boarding pendente',
      pendingBoardingText: 'Transação de boarding confirmada on-chain.',
      preconfirmed: 'Pré-confirmada',
      received: 'Recebido',
      receivedSuccessfully: 'recebido com sucesso',
      receiverConfirmed: 'Recebimento confirmado',
      receiverConfirmedText: 'O recebimento foi confirmado com sucesso.',
      receiverNotConfirmed: 'Recebimento não confirmado',
      receiverNotConfirmedText: 'O recebimento ainda não foi confirmado.',
      recoverInProgress: 'Recuperação em andamento',
      recoverNotIncluded: 'Recuperação não incluída',
      recoverNothing: 'Nada a recuperar',
      recoverSwap: 'Recuperar swap',
      recoverSwapBody: 'Se o swap ainda estiver pendente, isso devolverá à sua carteira os fundos bloqueados.',
      recoverSwapTitle: 'Recuperar o swap?',
      recoverUnsupported: 'Recuperação não suportada',
      recoverable: 'Recuperável',
      recoverableText: 'Este swap pode ser recuperado.',
      recovering: 'Recuperando...',
      refunded: 'Reembolsada',
      renew: 'Renovar',
      retryCancel: 'Tentar cancelar novamente',
      retryRecover: 'Tentar recuperar novamente',
      sent: 'Enviado',
      settleTransaction: 'Liquidar transação',
      settled: 'Liquidada',
      settledSuccessfully: 'Transação liquidada com sucesso',
      settling: 'Liquidando...',
      soundsGood: 'Ótimo!',
      success: 'Sucesso',
      swap: 'Swap',
      swapCancelled: 'Swap cancelado',
      swapFailed: 'Falha no swap',
      swapPending: 'Swap pendente',
      swapRecoverable: 'Swap recuperável',
      toAssetId: 'ID do ativo de destino',
      toAssetIdUnverified: 'ID do ativo de destino (não verificado)',
      totalReceived: 'Total recebido',
      totalTicker: 'Total ({ticker})',
      unconfirmed: 'Não confirmada',
      unconfirmedText: 'Transação on-chain não confirmada. Aguarde a confirmação.',
    },
    unlock: {
      connectionFailed: 'Erro de conexão. Tente novamente.',
      enterPassword: 'Digite sua senha',
      insertPassword: 'Inserir senha',
      invalidPassword: 'Senha incorreta',
      passwordPlaceholder: 'senha',
      title: 'Desbloquear',
      unlockWallet: 'Desbloquear carteira',
      unlockWithPasskey: 'Desbloquear com sua chave de acesso',
    },
    upsells: {
      buySellDescription: 'Use uma transferência bancária em CHF ou EUR via SEPA.',
      buySellTitle: 'Comprar ou vender bitcoin',
      title: 'Faça mais com seu dinheiro',
    },
    vtxos: {
      automaticRenewal: 'A renovação automática ocorre para moedas que vencem em até {time}.',
      belowDustLimit: 'Abaixo do limite de dust',
      boardingUtxos: 'Seus UTXOs de boarding com valor e vencimento',
      coins: 'Moedas',
      coinsRenewed: 'Moedas renovadas com sucesso',
      coinsWithAmount: 'Suas moedas virtuais com valor e vencimento',
      completeBoarding: 'Concluir boarding',
      completeBoardingAndRenew: 'Concluir boarding e renovar',
      date: 'Data',
      expiringSoon: 'expira em breve',
      failedToFetchCoins: 'Erro ao buscar moedas',
      firstExpiration: 'Primeiro vencimento de moeda virtual: {time}.',
      nextMarketHour: 'Próximo horário de mercado: {date} ({ago}) por {duration}.',
      nextRenewal: 'Próxima renovação',
      renewVirtualCoins: 'Renovar moedas virtuais',
      renewing: 'Renovando',
      settled: 'liquidado',
      settlementFees: 'A liquidação durante o horário de mercado oferece taxas menores.',
      subdust: 'subdust',
      swept: 'recuperável',
      title: 'Moedas virtuais',
      unconfirmed: 'não confirmado',
      virtualCoinRenewal: 'Renovação de moeda virtual',
    },
    wallet: {
      accounts: 'Contas',
      activity: 'Atividade',
      addArkadeToHome: 'Adicionar Arkade à tela inicial',
      install: 'Instalar',
      installAndroidDescription: "Toque em 'Instalar' para adicionar Arkade à tela inicial.",
      installIosDescription: 'Toque no ícone de compartilhamento do Safari e depois em "Adicionar à Tela de Início".',
      openSettings: 'Abrir configurações',
      receive: 'Receber',
      recentActivity: 'Atividade recente',
      scan: 'Escanear',
      send: 'Enviar',
      swap: 'Trocar',
      syncing: 'Sincronizando...',
      tapToGoHome: 'Toque para ir à tela inicial.',
      transaction: 'Transação',
      viewAll: 'Ver tudo',
      viewAllActivity: 'Ver toda a atividade',
      viewRecentActivity: 'Ver atividade recente',
      walletActions: 'Ações da carteira',
    },
  },
} as const

export type TranslationKey = keyof typeof translations.en
export type TranslationDict = {
  [K in keyof typeof translations.en]: {
    [P in keyof (typeof translations.en)[K]]: string
  }
}

export function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    params[key] !== undefined ? String(params[key]) : match,
  )
}

export function getTranslationDict(language: Language): TranslationDict {
  if (language === Language.Spanish) return translations.es
  if (language === Language.Portuguese) return translations.pt
  return translations.en
}

export function translate(language: Language, key: string, params?: Record<string, string | number>): string {
  const dict = getTranslationDict(language)
  const template = key.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[part]
    }
    return undefined
  }, dict)
  if (typeof template !== 'string') return key
  return interpolate(template, params)
}
